/**
 * Admin.js - 관리자 페이지 (Admin)
 * DB관리 (The Core) - 모든 데이터를 직접 생성, 조회, 수정, 삭제할 수 있는 강력한 CRUD 인터페이스
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Checkbox,
  Paper,
  Tooltip,
  Autocomplete,
} from '@mui/material';
// react-window는 MUI Table과의 통합이 복잡하므로, 
// 대용량 데이터 처리 최적화는 백엔드 페이지네이션으로 해결
// 필요시 추후 별도 컴포넌트로 구현 가능
import { Database, AlertTriangle, Upload, Download, Trash2, Shield, FileText, Edit, Save, X, Bell, Plus, CheckCircle, XCircle } from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useMobileView } from '../contexts/MobileViewContext';
import { useDBConnection } from '../contexts/DBConnectionContext';
import axios from 'axios';
import { readExcelFile, readExcelFileAllSheets, downloadExcelTemplate } from '../utils/excelUtils';
import * as XLSX from 'xlsx';
import { notifyDeptChanged, notifyEmployeeChanged, notifyDeptEmployeeLinkChanged } from '../utils/dataChangeNotifier';
import ConfirmModal from '../components/ConfirmModal';
import apiConfig from '../config/api';
import notificationService from '../services/NotificationService';

const API_BASE_URL = apiConfig.V1;

const Admin = () => {
  const { colors, isDark } = useCustomTheme();
  const { isMobileView } = useMobileView();
  const { isConnected: dbContextConnected, connect: dbContextConnect, disconnect: dbContextDisconnect, serverInfo: dbContextServerInfo } = useDBConnection();
  const [tabValue, setTabValue] = useState(0); // 0: DB관리, 1: 권한 관리, 2: 접속 로그, 3: 알림 관리
  const [mobileWarningShown, setMobileWarningShown] = useState(false);
  const [mobileWarningDialogOpen, setMobileWarningDialogOpen] = useState(false);
  
  // 현재 사용자 권한 상태
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  
  // 권한 관리 관련 상태
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  // const [permissionChanges, setPermissionChanges] = useState({}); // 변경사항 추적 - 현재 사용되지 않음
  const [editingEmployee, setEditingEmployee] = useState(null); // 편집 중인 직원 코드
  const [editPermissions, setEditPermissions] = useState({}); // 편집 중인 권한 정보 (임시 저장)
  
  // 접속 로그 관련 상태
  const [accessLogs, setAccessLogs] = useState([]);
  const [isLoadingAccessLogs, setIsLoadingAccessLogs] = useState(false);
  const [accessLogEmployees, setAccessLogEmployees] = useState([]); // 접속 로그용 직원 목록
  const [accessLogFilter, setAccessLogFilter] = useState({
    employee_code: '',
    start_date: '',
    end_date: ''
  });
  const [accessLogTotal, setAccessLogTotal] = useState(0);
  
  // 알림 관리 관련 상태
  const [announcements, setAnnouncements] = useState([]);
  const [isLoadingAnnouncements, setIsLoadingAnnouncements] = useState(false);
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    message: '',
    start_date: '',
    end_date: '',
    is_active: true
  });
  
  // 알림 시간 설정 관련 상태 (초기값: 빈 배열 - 알림 비활성화 상태)
  const [notificationTimes, setNotificationTimes] = useState([]);
  const [isLoadingNotificationTimes, setIsLoadingNotificationTimes] = useState(false);
  const [notificationTimeDialogOpen, setNotificationTimeDialogOpen] = useState(false);
  // 다이얼로그 내부에서 사용할 로컬 상태 (다이얼로그가 열릴 때마다 최신 값으로 초기화)
  const [dialogNotificationTimes, setDialogNotificationTimes] = useState([]);
  const [excludeSaturday, setExcludeSaturday] = useState(false);  // 토요일 제외 여부
  const [excludeSunday, setExcludeSunday] = useState(false);  // 일요일 제외 여부

  // FCM 테스트 관련 상태
  const [fcmTestEmployeeCode, setFcmTestEmployeeCode] = useState('');
  const [fcmTestTitle, setFcmTestTitle] = useState('OnTimeLog 테스트 알림');
  const [fcmTestBody, setFcmTestBody] = useState('관리자 테스트 알림입니다.');
  const [fcmTestStatus, setFcmTestStatus] = useState(null);
  const [isSavingFcmToken, setIsSavingFcmToken] = useState(false);
  const [isSendingFcmTest, setIsSendingFcmTest] = useState(false);

  const notificationPermission = typeof Notification !== 'undefined'
    ? Notification.permission
    : 'unsupported';
  
  // 모바일 뷰에서 Admin 페이지 접근 시 경고
  React.useEffect(() => {
    if (isMobileView && !mobileWarningShown && tabValue === 0) { // DB관리 탭 (이제 유일한 탭)
      setMobileWarningDialogOpen(true);
      setMobileWarningShown(true);
    }
  }, [isMobileView, tabValue, mobileWarningShown]);
  // const [editingCell, setEditingCell] = useState(null); // 현재 사용되지 않음
  // const [editValue, setEditValue] = useState(''); // 현재 사용되지 않음
  // const [newRow, setNewRow] = useState(null); // 현재 사용되지 않음
  
  // DB관리에서 사용할 데이터 (기준정보는 DB관리에서 직접 관리)
  
  // DB관리 관련 상태
  const [dbConnectionInfo, setDbConnectionInfo] = useState({
    host: 'localhost',
    port: 3306,
    database: '',
    username: '',
    password: '',
    dbType: 'mysql', // mysql, mssql, postgresql
  });
  // DB 연결 상태는 DBConnectionContext와 동기화
  // DBConnectionContext의 상태를 직접 사용하여 다른 탭/메뉴로 이동해도 상태 유지
  const [dbConnected, setDbConnected] = useState(dbContextConnected);
  
  // DBConnectionContext 상태와 동기화 (다른 탭/메뉴로 이동해도 상태 유지)
  useEffect(() => {
    // DBConnectionContext의 연결 상태가 변경되면 로컬 상태도 업데이트
    if (dbContextConnected !== dbConnected) {
      console.log('[DB관리] DBConnectionContext 상태 동기화:', { 
        dbContextConnected, 
        dbConnected,
        serverInfo: dbContextServerInfo,
        hasPassword: !!getStoredPassword()
      });
      
      // 연결이 활성화되는 경우
      if (dbContextConnected && dbContextServerInfo) {
        // 연결 정보 복원
        const serverParts = dbContextServerInfo.server?.split(':') || [];
        setDbConnectionInfo({
          host: serverParts[0] || 'localhost',
          port: parseInt(serverParts[1]) || 3306,
          database: dbContextServerInfo.database || '',
          username: dbContextServerInfo.username || '',
          password: '',
          dbType: 'mysql'
        });
        
        // 비밀번호가 sessionStorage에 있으면 연결 상태 유지
        const storedPassword = getStoredPassword();
        if (storedPassword) {
          console.log('[DB관리] 저장된 비밀번호로 연결 상태 유지');
          setDbConnected(true);
          localStorage.setItem('db_connected', 'true');
        } else {
          // 비밀번호가 없으면 연결 상태만 업데이트 (비밀번호 입력 필요)
          setDbConnected(true);
          localStorage.setItem('db_connected', 'true');
        }
      } else if (!dbContextConnected) {
        // 연결이 해제되면 테이블 데이터도 초기화
        setDbConnected(false);
        setDbTables([]);
        setSelectedTable(null);
        setTableData([]);
        setTableColumns([]);
        sessionStorage.removeItem('db_password_temp');
        localStorage.setItem('db_connected', 'false');
      }
    }
  }, [dbContextConnected, dbContextServerInfo]);
  const [dbTables, setDbTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [downloadFormat, setDownloadFormat] = useState('xlsx'); // xlsx 또는 csv
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingIntegrated, setIsExportingIntegrated] = useState(false); // V30 통합 리포트
  const [tableColumnInfo, setTableColumnInfo] = useState([]); // 컬럼 상세 정보 (타입, NULL 허용 등)
  const [primaryKeys, setPrimaryKeys] = useState([]); // PRIMARY KEY 목록
  const [editingRowIndex, setEditingRowIndex] = useState(null); // 편집 중인 행 인덱스
  const [editingRowData, setEditingRowData] = useState({}); // 편집 중인 행 데이터
  const [newRowData, setNewRowData] = useState({}); // 새 행 데이터
  const [isAddingRow, setIsAddingRow] = useState(false); // 새 행 추가 모드
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmCallback, setDeleteConfirmCallback] = useState(null);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');
  const [rowToDelete, setRowToDelete] = useState(null); // 삭제할 행 데이터
  const [connectionDialogOpen, setConnectionDialogOpen] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [isLoadingTables, setIsLoadingTables] = useState(false);
  const [isLoadingTableData, setIsLoadingTableData] = useState(false);
  // 에러 모달 상태
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [lastErrorTime, setLastErrorTime] = useState(0); // 중복 에러 방지용
  const [uploadAllDialogOpen, setUploadAllDialogOpen] = useState(false);
  const [uploadAllResults, setUploadAllResults] = useState(null);
  const [isUploadingAll, setIsUploadingAll] = useState(false);
  
  // 일괄 업로드 미리보기 관련 상태
  const [uploadPreviewDialogOpen, setUploadPreviewDialogOpen] = useState(false);
  const [uploadPreviewData, setUploadPreviewData] = useState(null); // { tableName: [데이터 배열] }
  const [selectedPreviewTable, setSelectedPreviewTable] = useState(null);
  const [pendingUploadFile, setPendingUploadFile] = useState(null); // 실제 업로드할 파일 저장
  const [selectedTablesForUpload, setSelectedTablesForUpload] = useState(new Set()); // 선택된 테이블 목록 (체크박스)
  
  // 컬럼 너비 설정 (DB관리에서 사용) - 현재 사용되지 않음
  // const [columnWidths, setColumnWidths] = useState({});

  // 모든 사용자에게 DB 연결 자동 활성화 (한 번만 실행)
  const autoConnectExecutedRef = React.useRef(false);

  // 비밀번호 조회 헬퍼 함수 (만료 여부 확인)
  const getStoredPassword = () => {
    try {
      const stored = sessionStorage.getItem('db_password_temp');
      if (!stored) {
        return null;
      }
      
      // 기존 형식(문자열)인지 새 형식(객체)인지 확인
      let passwordData;
      try {
        passwordData = JSON.parse(stored);
        // 새 형식: 만료 시간 확인
        if (passwordData.expiresAt) {
          const now = Date.now();
          if (now > passwordData.expiresAt) {
            // 만료됨
            console.log('[DB관리] 저장된 비밀번호가 만료되었습니다.');
            sessionStorage.removeItem('db_password_temp');
            return null;
          }
          return passwordData.password;
        }
        // 만료 시간이 없으면 기존 형식으로 간주하고 그대로 반환
        return passwordData;
      } catch {
        // JSON 파싱 실패 시 기존 형식(문자열)으로 간주
        return stored;
      }
    } catch (error) {
      console.error('[DB관리] 비밀번호 조회 실패:', error);
      return null;
    }
  };
  
  // 컴포넌트 마운트 시 비밀번호 만료 여부 확인 및 자동 연결
  useEffect(() => {
    // 비밀번호 만료 여부 확인
    const storedPassword = getStoredPassword();
    if (!storedPassword && dbConnected) {
      // 비밀번호가 만료되었거나 없으면 연결 상태 해제
      console.log('[DB관리] 컴포넌트 마운트 시 비밀번호 만료 확인: 연결 해제됨');
      setDbConnected(false);
      localStorage.setItem('db_connected', 'false');
      // 테이블 목록도 초기화
      setDbTables([]);
      setSelectedTable(null);
      setTableData([]);
      setTableColumns([]);
    } else if (storedPassword && !dbConnected) {
      // 비밀번호가 유효한데 연결 상태가 해제되어 있으면 복원
      console.log('[DB관리] 컴포넌트 마운트 시 비밀번호 유효 확인: 연결 상태 복원');
      setDbConnected(true);
      localStorage.setItem('db_connected', 'true');
    }
    
    // 주기적으로 비밀번호 만료 여부 확인 (5분마다)
    const passwordCheckInterval = setInterval(() => {
      const currentPassword = getStoredPassword();
      if (!currentPassword && dbConnected) {
        console.log('[DB관리] 주기적 비밀번호 만료 확인: 연결 해제됨');
        setDbConnected(false);
        localStorage.setItem('db_connected', 'false');
        setDbTables([]);
        setSelectedTable(null);
        setTableData([]);
        setTableColumns([]);
      }
    }, 5 * 60 * 1000); // 5분마다 확인
    
    // 모든 사용자(User, Admin, Root 포함)에게 DB 연결 자동 활성화
    if (!autoConnectExecutedRef.current) {
      const savedConnectionInfo = localStorage.getItem('db_connection_info');
      
      if (savedConnectionInfo) {
        try {
          const parsed = JSON.parse(savedConnectionInfo);
          if (parsed.database && parsed.host) {
            if (process.env.NODE_ENV === 'development') {
              console.log('🔄 [DEBUG] 자동 연결 시작 (모든 사용자)', { 
                parsed: { ...parsed, password: '***' },
                userRole: localStorage.getItem('userRole') || 'user'
              });
            }
            
            // 연결 정보 설정 (비밀번호는 제외)
            setDbConnectionInfo({
              ...parsed,
              password: '' // 비밀번호는 보안상 저장하지 않음
            });
            
            // DBConnectionContext를 통해 연결 상태 활성화
            const serverDisplay = `${parsed.host}:${parsed.port || 3306}`;
            dbContextConnect(serverDisplay, parsed.database, parsed.username || '');
            
            // 실행 플래그 설정 (한 번만 실행)
            autoConnectExecutedRef.current = true;
          }
        } catch (e) {
          console.error('연결 정보 복원 실패:', e);
        }
      } else {
        // 저장된 연결 정보가 없으면 DBConnectionContext의 상태를 확인
        // DBConnectionContext는 이미 health check를 통해 연결 상태를 확인했을 수 있음
        // 약간의 지연을 두어 DBConnectionContext가 초기화될 시간을 줌
        const checkContext = () => {
          if (dbContextConnected && dbContextServerInfo) {
            if (process.env.NODE_ENV === 'development') {
              console.log('🔄 [DEBUG] DBConnectionContext에서 연결 정보 복원', {
                serverInfo: dbContextServerInfo
              });
            }
            
            const serverParts = dbContextServerInfo.server?.split(':') || [];
            setDbConnectionInfo({
              host: serverParts[0] || 'localhost',
              port: parseInt(serverParts[1]) || 3306,
              database: dbContextServerInfo.database || '',
              username: dbContextServerInfo.username || '',
              password: '',
              dbType: 'mysql'
            });
            
            // DBConnectionContext에서 연결 상태가 활성화되어 있으면 로컬 상태도 업데이트
            setDbConnected(true);
            localStorage.setItem('db_connected', 'true');
            
            autoConnectExecutedRef.current = true;
          } else if (!autoConnectExecutedRef.current) {
            // DBConnectionContext가 아직 준비되지 않았으면 100ms 후 다시 시도
            setTimeout(checkContext, 100);
          }
        };
        
        // 즉시 확인 및 지연 확인
        checkContext();
      }
    }
    
    // cleanup 함수: 주기적 확인 인터벌 제거
    return () => {
      clearInterval(passwordCheckInterval);
    };
  }, []); // 빈 의존성 배열 - 컴포넌트 마운트 시 한 번만 실행
  
  // 탭 변경 시 데이터 로드 및 비밀번호 만료 여부 확인
  useEffect(() => {
    // DB관리 탭으로 돌아올 때 DBConnectionContext 상태 확인
    if (tabValue === 0) {
      // DBConnectionContext에서 연결 상태가 활성화되어 있고 비밀번호가 있으면 연결 상태 유지
      if (dbContextConnected && dbContextServerInfo) {
        const storedPassword = getStoredPassword();
        if (storedPassword) {
          // 비밀번호가 있으면 연결 상태 유지 (모달 열지 않음)
          console.log('[DB관리] 탭 변경 시: 저장된 비밀번호로 연결 상태 유지');
          setDbConnected(true);
          localStorage.setItem('db_connected', 'true');
          
          // 연결 정보 복원
          const serverParts = dbContextServerInfo.server?.split(':') || [];
          setDbConnectionInfo({
            host: serverParts[0] || 'localhost',
            port: parseInt(serverParts[1]) || 3306,
            database: dbContextServerInfo.database || '',
            username: dbContextServerInfo.username || '',
            password: '',
            dbType: 'mysql'
          });
        } else {
          // 비밀번호가 없으면 연결 상태는 유지하되 비밀번호 입력 필요
          console.log('[DB관리] 탭 변경 시: 비밀번호 없음, 연결 상태는 유지');
          setDbConnected(true);
          localStorage.setItem('db_connected', 'true');
        }
      } else {
        // DBConnectionContext에서 연결이 해제되어 있으면 로컬 상태도 해제
        const storedPassword = getStoredPassword();
        if (!storedPassword && dbConnected) {
          console.log('[DB관리] 탭 변경 시 비밀번호 만료 확인: 연결 해제됨');
          setDbConnected(false);
          localStorage.setItem('db_connected', 'false');
          setDbTables([]);
          setSelectedTable(null);
          setTableData([]);
          setTableColumns([]);
        }
      }
    }
    
    if (tabValue === 0) {
      // DB관리 탭: 저장된 연결정보 로드 (자동 연결과 별도로 실행)
      loadSavedConnectionInfo();
      fetchCurrentUser();
    } else if (tabValue === 1) {
      // 권한 관리 탭: 데이터 로드
      fetchPermissionData();
    } else if (tabValue === 2) {
      // 접속 로그 탭: 데이터 로드
      fetchAccessLogEmployees();
      fetchAccessLogs();
    } else if (tabValue === 3) {
      // 알림 관리 탭: 데이터 로드
      fetchAnnouncements();
    }
    
    // 탭이 변경되면 autoConnectExecutedRef 초기화
    if (tabValue !== 0) {
      autoConnectExecutedRef.current = false;
    }
  }, [tabValue]);

  // DB 연결 상태가 변경되면 테이블 목록 자동 로드
  // 이전 실행 상태를 추적하여 무한 루프 방지
  const prevDbStateRef = React.useRef({ 
    dbConnected: false, 
    database: '', 
    hasPassword: false,
    executed: false // 실행 여부 플래그 추가
  });
  
  // fetchDbTables 함수 참조를 저장하기 위한 ref
  const fetchDbTablesRef = React.useRef(null);
  
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 [DEBUG] useEffect 실행됨', {
          timestamp: new Date().toISOString(),
          tabValue,
          dbConnected,
          database: dbConnectionInfo.database,
          prevState: { ...prevDbStateRef.current },
          fetchDbTablesRefExists: !!fetchDbTablesRef.current
        });
      }
    }
    
    // 탭이 DB관리 탭일 때만 실행
    if (tabValue === 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 [DEBUG] DB관리 탭 활성화됨');
      }
      
      if (dbConnected && dbConnectionInfo.database) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 [DEBUG] DB 연결 상태 확인됨', {
            dbConnected,
            database: dbConnectionInfo.database,
            host: dbConnectionInfo.host,
            username: dbConnectionInfo.username
          });
        }
        
        // 비밀번호가 있을 때만 테이블 목록 조회 (에러 방지)
        const tempPassword = getStoredPassword() || dbConnectionInfo.password || '';
        const hasPassword = Boolean(tempPassword);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 [DEBUG] 비밀번호 확인', {
            hasPassword,
            hasTempPassword: !!getStoredPassword(),
            hasDbPassword: !!dbConnectionInfo.password,
            passwordLength: tempPassword.length
          });
        }
        
        // 상태가 실제로 변경되었을 때만 실행 (무한 루프 방지)
        const prevState = prevDbStateRef.current;
        const stateChanged = 
          prevState.dbConnected !== dbConnected ||
          prevState.database !== dbConnectionInfo.database ||
          prevState.hasPassword !== hasPassword;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 [DEBUG] 상태 변경 체크', {
            stateChanged,
            prevState: {
              dbConnected: prevState.dbConnected,
              database: prevState.database,
              hasPassword: prevState.hasPassword,
              executed: prevState.executed
            },
            currentState: {
              dbConnected,
              database: dbConnectionInfo.database,
              hasPassword
            },
            changes: {
              dbConnected: prevState.dbConnected !== dbConnected,
              database: prevState.database !== dbConnectionInfo.database,
              hasPassword: prevState.hasPassword !== hasPassword
            }
          });
        }
        
        // 상태가 변경되었을 때 실행
        // executed 플래그는 비밀번호가 없었을 때만 true로 유지 (무한 루프 방지)
        // 비밀번호가 있고 연결되었을 때는 항상 실행 가능하도록 함
        const shouldExecute = stateChanged && (hasPassword || !prevState.executed);
        
        if (shouldExecute) {
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ [DEBUG] 상태 변경 감지 및 실행 시작', {
              executed: prevState.executed,
              hasPassword,
              willExecute: true
            });
          }
          
          // 실행 플래그 설정 (비밀번호가 없으면 executed=true로 설정하여 무한 루프 방지)
          prevDbStateRef.current = {
            dbConnected,
            database: dbConnectionInfo.database,
            hasPassword,
            executed: !hasPassword // 비밀번호가 없을 때만 executed=true
          };
          
          if (process.env.NODE_ENV === 'development') {
            console.log('🔍 [DEBUG] executed 플래그 설정됨', {
              newState: { ...prevDbStateRef.current },
              executed: !hasPassword
            });
          }
          
          if (hasPassword) {
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ [DEBUG] 비밀번호 있음 - fetchDbTables 호출 시도');
            }
            
            // 연결 정보가 준비되고 비밀번호가 있으면 테이블 목록 조회
            // ref를 통해 함수 호출 (초기화 순서 문제 방지)
            if (fetchDbTablesRef.current) {
              if (process.env.NODE_ENV === 'development') {
                console.log('✅ [DEBUG] fetchDbTablesRef.current 존재 - 함수 호출');
              }
              fetchDbTablesRef.current().finally(() => {
                if (process.env.NODE_ENV === 'development') {
                  console.log('✅ [DEBUG] fetchDbTables 완료');
                }
              });
            } else {
              if (process.env.NODE_ENV === 'development') {
                console.warn('⚠️ [DEBUG] fetchDbTablesRef.current가 null입니다!');
              }
            }
          } else {
            if (process.env.NODE_ENV === 'development') {
              console.log('⚠️ [DEBUG] 비밀번호 없음 - 테이블 목록 조회 스킵');
            }
            
            // 비밀번호가 없으면 조용히 스킵 (에러 발생하지 않음)
            // 무한 루프 방지를 위해 로그는 한 번만 출력
            if (!prevState.dbConnected || prevState.database !== dbConnectionInfo.database) {
              if (process.env.NODE_ENV === 'development') {
                console.log('[DB관리] 비밀번호가 없어 테이블 목록을 자동으로 가져오지 않습니다.');
              }
            }
          }
        } else {
          if (process.env.NODE_ENV === 'development') {
            if (!stateChanged) {
              console.log('⏭️ [DEBUG] 상태 변경 없음 - 실행 스킵', {
                executed: prevState.executed
              });
            } else if (prevState.executed && !hasPassword) {
              console.log('⏭️ [DEBUG] 이미 실행됨 (executed=true, 비밀번호 없음) - 실행 스킵');
            } else if (prevState.executed && hasPassword) {
              // 비밀번호가 생겼는데 executed=true인 경우, 리셋하고 실행
              console.log('🔄 [DEBUG] 비밀번호 생김 - executed 플래그 리셋 및 실행');
            }
          }
          
          if (prevState.executed && hasPassword) {
            prevDbStateRef.current.executed = false;
            if (fetchDbTablesRef.current) {
              fetchDbTablesRef.current();
            }
          }
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 [DEBUG] DB 연결 조건 불만족', {
            dbConnected,
            hasDatabase: !!dbConnectionInfo.database
          });
        }
        
        // 연결 해제 시 상태 업데이트 (executed 플래그는 유지하여 무한 루프 방지)
        if (prevDbStateRef.current.dbConnected !== dbConnected) {
          if (process.env.NODE_ENV === 'development') {
            console.log('🔄 [DEBUG] DB 연결 상태 변경 감지 - 상태만 업데이트 (executed 플래그 유지)');
          }
          prevDbStateRef.current = {
            ...prevDbStateRef.current,
            dbConnected: false,
            database: '',
            hasPassword: false
            // executed 플래그는 유지하여 무한 루프 방지
          };
        }
        // 연결 해제 시 테이블 목록 초기화하지 않음 (탭 전환 시에도 유지)
        // setDbTables([]);
        // setSelectedTable(null);
      }
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 [DEBUG] 다른 탭 활성화됨 - executed 플래그 리셋', {
          tabValue,
          prevExecuted: prevDbStateRef.current.executed
        });
      }
      
      // 다른 탭으로 전환 시 executed 플래그 리셋
      prevDbStateRef.current.executed = false;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 [DEBUG] useEffect 종료', {
        finalState: { ...prevDbStateRef.current }
      });
    }
  }, [dbConnected, dbConnectionInfo.database, tabValue]);
  
  // 현재 사용자 정보 조회
  const fetchCurrentUser = async () => {
    try {
      // localStorage에서 사용자 정보 가져오기
      const token = localStorage.getItem('token');
      const email = localStorage.getItem('email') || localStorage.getItem('userEmail');
      
      if (email) {
        setCurrentUserEmail(email);
        // 백엔드에서 현재 사용자 정보 조회
        try {
          const response = await axios.get(`${API_BASE_URL}/admin/employees`, {
            headers: {
              'Authorization': `Bearer ${token || email}`
            }
          });
          const employeesData = response.data || [];
          const currentUser = employeesData.find(emp => emp.email === email);
          if (currentUser) {
            setCurrentUserRole(currentUser.role || 'User');
          } else {
            // 사용자를 찾을 수 없으면 localStorage에서 가져오기
            const savedRole = localStorage.getItem('userRole');
            setCurrentUserRole(savedRole === 'admin' ? 'Admin' : savedRole === 'root' ? 'Root' : 'User');
          }
        } catch (error) {
          // API 실패 시 localStorage에서 가져오기
          const savedRole = localStorage.getItem('userRole');
          setCurrentUserRole(savedRole === 'admin' ? 'Admin' : savedRole === 'root' ? 'Root' : 'User');
        }
      } else {
        // 이메일이 없으면 기본값
        const savedRole = localStorage.getItem('userRole');
        setCurrentUserRole(savedRole === 'admin' ? 'Admin' : savedRole === 'root' ? 'Root' : 'User');
      }
    } catch (error) {
      console.error('현재 사용자 정보 조회 실패:', error);
      const savedRole = localStorage.getItem('userRole');
      setCurrentUserRole(savedRole === 'admin' ? 'Admin' : savedRole === 'root' ? 'Root' : 'User');
    }
  };

  // 권한 관리 데이터 조회
  const fetchPermissionData = async () => {
    setIsLoadingPermissions(true);
    try {
      // 현재 사용자 정보 먼저 조회
      await fetchCurrentUser();
      
      // 직원 목록 조회
      const employeesRes = await axios.get(`${API_BASE_URL}/admin/employees`);
      const employeesData = employeesRes.data || [];
      
      // 부서 목록 조회
      const deptsRes = await axios.get(`${API_BASE_URL}/admin/departments`);
      const deptsData = deptsRes.data || [];
      
      // Manager 목록 조회
      const managersRes = await axios.get(`${API_BASE_URL}/admin/managers`);
      const managersData = managersRes.data || [];
      
      // 각 직원의 Manager 권한 정보 매핑
      const employeesWithPermissions = employeesData.map(emp => {
        // 해당 직원이 Manager인 부서 목록
        const managerDepts = managersData
          .filter(mgr => mgr.employee_code === emp.employee_code)
          .map(mgr => mgr.dept_code);
        
        // 각 부서별 Manager 여부
        const deptManagerMap = {};
        deptsData.forEach(dept => {
          deptManagerMap[dept.dept_code] = managerDepts.includes(dept.dept_code);
        });
        
        return {
          ...emp,
          isAdmin: emp.role === 'Admin' || emp.role === 'Root',
          isManager: managerDepts.length > 0,
          deptManagerMap: deptManagerMap,
        };
      });
      
      setEmployees(employeesWithPermissions);
      setDepartments(deptsData);
    } catch (error) {
      console.error('권한 데이터 조회 실패:', error);
      alert(`권한 데이터를 가져오는데 실패했습니다: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoadingPermissions(false);
    }
  };
  
  // Admin 권한 변경 (Root만 가능)
  const handleAdminToggle = async (employeeCode, isAdmin) => {
    // Root 권한이 아니면 실행하지 않음
    if (currentUserRole !== 'Root') {
      alert('Admin 권한 변경은 Root 권한만 가능합니다.');
      return;
    }
    
    try {
      await axios.post(`${API_BASE_URL}/root/admins/assign`, {
        employee_code: employeeCode,
        role: isAdmin ? 'Admin' : 'User'
      });
      
      // 로컬 상태 업데이트
      setEmployees(prev => prev.map(emp => 
        emp.employee_code === employeeCode
          ? { ...emp, isAdmin: isAdmin, role: isAdmin ? 'Admin' : 'User' }
          : emp
      ));
    } catch (error) {
      console.error('Admin 권한 변경 실패:', error);
      alert(`Admin 권한 변경 실패: ${error.response?.data?.detail || error.message}`);
    }
  };
  
  // 접속 로그용 직원 목록 조회
  // 알림 관리 관련 함수
  const fetchAnnouncements = async () => {
    setIsLoadingAnnouncements(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/admin/announcements`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { include_inactive: true }
      });
      setAnnouncements(response.data || []);
    } catch (error) {
      console.error('공지사항 조회 실패:', error);
      alert(`공지사항 조회 실패: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoadingAnnouncements(false);
    }
  };

  const handleCreateAnnouncement = () => {
    setEditingAnnouncement(null);
    setAnnouncementForm({
      title: '',
      message: '',
      start_date: '',
      end_date: '',
      is_active: true
    });
    setAnnouncementDialogOpen(true);
  };

  const handleEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement);
    setAnnouncementForm({
      title: announcement.title,
      message: announcement.message,
      start_date: announcement.start_date,
      end_date: announcement.end_date,
      is_active: announcement.is_active
    });
    setAnnouncementDialogOpen(true);
  };

  const handleSaveAnnouncement = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      if (editingAnnouncement) {
        // 수정
        await axios.put(
          `${API_BASE_URL}/admin/announcements/${editingAnnouncement.announcement_id}`,
          announcementForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('공지사항이 수정되었습니다.');
      } else {
        // 생성
        await axios.post(
          `${API_BASE_URL}/admin/announcements`,
          announcementForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('공지사항이 생성되었습니다.');
      }
      
      setAnnouncementDialogOpen(false);
      fetchAnnouncements();
    } catch (error) {
      console.error('공지사항 저장 실패:', error);
      alert(`공지사항 저장 실패: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('정말 이 공지사항을 삭제하시겠습니까?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/admin/announcements/${announcementId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('공지사항이 삭제되었습니다.');
      fetchAnnouncements();
    } catch (error) {
      console.error('공지사항 삭제 실패:', error);
      alert(`공지사항 삭제 실패: ${error.response?.data?.detail || error.message}`);
    }
  };

  // 알림 시간 설정 관련 함수
  const fetchNotificationTimes = async () => {
    setIsLoadingNotificationTimes(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // 디버깅: 요청 정보 로그
      console.log('[Admin] 알림 시간 설정 조회 시도:', {
        url: `${API_BASE_URL}/admin/notification-times`,
        hasToken: !!token
      });
      
      const response = await axios.get(`${API_BASE_URL}/admin/notification-times`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[Admin] 알림 시간 설정 조회 성공:', response.data);
      
      // 설정된 시간이 없으면 빈 배열 (알림 비활성화)
      const times = response.data.times || [];
      const excludeSat = response.data.exclude_saturday || false;
      const excludeSun = response.data.exclude_sunday || false;
      setNotificationTimes(times);
      setExcludeSaturday(excludeSat);
      setExcludeSunday(excludeSun);
      return { times, excludeSaturday: excludeSat, excludeSunday: excludeSun }; // 반환값 수정
    } catch (error) {
      console.error('[Admin] 알림 시간 설정 조회 실패:', {
        error: error,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method
      });
      // 에러 발생 시 빈 배열로 설정 (알림 비활성화)
      setNotificationTimes([]);
      setExcludeSaturday(false);
      setExcludeSunday(false);
      return { times: [], excludeSaturday: false, excludeSunday: false }; // 반환값 수정
    } finally {
      setIsLoadingNotificationTimes(false);
    }
  };

  const handleSaveNotificationTimes = async () => {
    console.log('[Admin] handleSaveNotificationTimes 함수 호출됨', {
      dialogNotificationTimes,
      API_BASE_URL,
      count: dialogNotificationTimes.length
    });
    
    // 로딩 상태 설정
    setIsLoadingNotificationTimes(true);
    
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      if (!token) {
        setIsLoadingNotificationTimes(false);
        setNotificationTimeDialogOpen(false);
        // 토큰 없음은 중요한 에러이므로 alert 유지
        alert('인증 토큰이 없습니다. 다시 로그인해주세요.');
        return;
      }
      
      // 디버깅: 요청 정보 로그
      console.log('[Admin] 알림 시간 설정 저장 시도:', {
        url: `${API_BASE_URL}/admin/notification-times`,
        times: dialogNotificationTimes,
        hasToken: !!token,
        tokenLength: token ? token.length : 0
      });
      
      const response = await axios.put(
        `${API_BASE_URL}/admin/notification-times`,
        { times: dialogNotificationTimes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('[Admin] 알림 시간 설정 저장 성공:', response.data);
      
      // 저장 응답에서 직접 상태 업데이트 (서버에서 반환된 값 사용)
      const savedTimes = response.data.times || [];
      const savedExcludeSat = response.data.exclude_saturday || false;
      const savedExcludeSun = response.data.exclude_sunday || false;
      setNotificationTimes(savedTimes);
      setExcludeSaturday(savedExcludeSat);
      setExcludeSunday(savedExcludeSun);
      
      console.log('[Admin] 상태 업데이트 완료:', {
        savedTimes,
        count: savedTimes.length
      });
      
      // 로딩 상태 해제 및 다이얼로그 닫기 (즉시 UI 반응)
      setIsLoadingNotificationTimes(false);
      setNotificationTimeDialogOpen(false);
      
      // 스케줄러 재시작을 비동기로 처리 (저장 완료를 기다리지 않음)
      // DB 저장이 완전히 반영될 시간을 확보하기 위해 충분한 지연을 둠
      setTimeout(() => {
        if (window.notificationService) {
          try {
            window.notificationService.stopDailyTimeNotificationScheduler();
            // 스케줄러 재시작을 더 긴 지연을 두어 DB 저장이 완전히 반영된 후 실행
            // 2초 지연으로 DB 커밋이 완료되도록 보장
            setTimeout(() => {
              window.notificationService.startDailyTimeNotificationScheduler();
              if (process.env.NODE_ENV === 'development') {
                console.log('[Admin] 알림 스케줄러 재시작 완료');
              }
            }, 2000);
          } catch (error) {
            console.error('[Admin] 스케줄러 재시작 실패:', error);
          }
        } else {
          // notificationService가 전역에 없으면 페이지 새로고침 (드물게 발생)
          if (process.env.NODE_ENV === 'development') {
            console.log('[Admin] notificationService를 찾을 수 없어 페이지를 새로고침합니다.');
          }
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      }, 100);
    } catch (error) {
      console.error('[Admin] 알림 시간 설정 저장 실패:', {
        error: error,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method
      });
      setIsLoadingNotificationTimes(false);
      // 에러는 중요한 정보이므로 alert 유지
      alert(`알림 시간 설정 저장 실패: ${error.response?.data?.detail || error.message || '알 수 없는 오류가 발생했습니다.'}`);
    }
  };

  const handleAddNotificationTime = () => {
    // 다이얼로그 내부 상태에 추가
    setDialogNotificationTimes([...dialogNotificationTimes, { hour: 9, minute: 0 }]);
  };

  const handleRemoveNotificationTime = (index) => {
    // 다이얼로그 내부 상태에서 제거 (빈 배열 허용 - 알림 비활성화 가능)
    setDialogNotificationTimes(dialogNotificationTimes.filter((_, i) => i !== index));
  };

  // 직접 저장 함수 (뱃지에서 삭제 시 즉시 저장)
  const handleSaveNotificationTimesDirect = async (timesToSave) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      if (!token) {
        console.error('[Admin] 인증 토큰이 없습니다.');
        return;
      }
      
      const response = await axios.put(
        `${API_BASE_URL}/admin/notification-times`,
        { times: timesToSave },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // 저장 응답에서 직접 상태 업데이트
      const savedTimes = response.data.times || [];
      setNotificationTimes(savedTimes);
      
      // 스케줄러 재시작
      if (window.notificationService) {
        setTimeout(() => {
          window.notificationService.stopDailyTimeNotificationScheduler();
          setTimeout(() => {
            window.notificationService.startDailyTimeNotificationScheduler();
          }, 2000);
        }, 100);
      }
    } catch (error) {
      console.error('[Admin] 알림 시간 설정 저장 실패:', error);
      // 에러 발생 시 원래 상태로 복원
      await fetchNotificationTimes();
    }
  };

  const handleUpdateNotificationTime = (index, field, value) => {
    // 다이얼로그 내부 상태 업데이트
    const updated = [...dialogNotificationTimes];
    updated[index] = { ...updated[index], [field]: parseInt(value) || 0 };
    setDialogNotificationTimes(updated);
  };

  const getCurrentEmployeeCode = () => {
    return (
      notificationService.getCurrentUserId?.() ||
      localStorage.getItem('employeeCode') ||
      ''
    );
  };

  const handleUseMyEmployeeCode = () => {
    const code = getCurrentEmployeeCode();
    if (!code) {
      setFcmTestStatus({
        severity: 'warning',
        message: '현재 로그인 계정의 직원 코드를 찾을 수 없습니다.'
      });
      return;
    }
    setFcmTestEmployeeCode(code);
    setFcmTestStatus({
      severity: 'info',
      message: `내 계정(${code})으로 대상이 설정되었습니다.`
    });
  };

  const handleRegisterFcmToken = async () => {
    setIsSavingFcmToken(true);
    setFcmTestStatus(null);
    try {
      const userId = getCurrentEmployeeCode();
      if (!userId) {
        setFcmTestStatus({
          severity: 'warning',
          message: '로그인 사용자 정보를 확인할 수 없습니다. 다시 로그인해주세요.'
        });
        return;
      }

      const token = await notificationService.requestFCMToken(userId);
      if (token) {
        setFcmTestStatus({
          severity: 'success',
          message: 'FCM 토큰이 등록/갱신되었습니다.'
        });
      } else {
        setFcmTestStatus({
          severity: 'warning',
          message: 'FCM 토큰을 발급하지 못했습니다. 알림 권한 및 Firebase 설정을 확인해주세요.'
        });
      }
    } catch (error) {
      console.error('[Admin] FCM 토큰 등록 실패:', error);
      setFcmTestStatus({
        severity: 'error',
        message: `FCM 토큰 등록 실패: ${error.message || '알 수 없는 오류'}`
      });
    } finally {
      setIsSavingFcmToken(false);
    }
  };

  const handleSendFcmTest = async () => {
    if (!fcmTestEmployeeCode) {
      setFcmTestStatus({
        severity: 'warning',
        message: '대상 직원 코드를 입력해주세요.'
      });
      return;
    }

    setIsSendingFcmTest(true);
    setFcmTestStatus(null);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        setFcmTestStatus({
          severity: 'error',
          message: '인증 토큰이 없습니다. 다시 로그인해주세요.'
        });
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/admin/notifications/test`,
        {
          employee_code: fcmTestEmployeeCode,
          title: fcmTestTitle,
          body: fcmTestBody
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const result = response.data;
      if (result?.success) {
        setFcmTestStatus({
          severity: 'success',
          message: result.message || 'FCM 테스트 알림 전송 완료'
        });
      } else {
        setFcmTestStatus({
          severity: 'warning',
          message: result?.message || 'FCM 테스트 알림 전송 실패'
        });
      }
    } catch (error) {
      console.error('[Admin] FCM 테스트 알림 실패:', error);
      setFcmTestStatus({
        severity: 'error',
        message: `FCM 테스트 알림 실패: ${error.response?.data?.detail || error.message || '알 수 없는 오류'}`
      });
    } finally {
      setIsSendingFcmTest(false);
    }
  };

  const fetchAccessLogEmployees = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/employees`);
      const employeesData = response.data || [];
      setAccessLogEmployees(employeesData);
    } catch (error) {
      console.error('직원 목록 조회 실패:', error);
    }
  };

  // 접속 로그 조회 함수
  const fetchAccessLogs = async () => {
    setIsLoadingAccessLogs(true);
    try {
      const params = {};
      if (accessLogFilter.employee_code) {
        params.employee_code = accessLogFilter.employee_code;
      }
      if (accessLogFilter.start_date) {
        params.start_date = accessLogFilter.start_date;
      }
      if (accessLogFilter.end_date) {
        params.end_date = accessLogFilter.end_date;
      }
      
      const response = await axios.get(`${API_BASE_URL}/admin/access-logs`, { params });
      if (response.data) {
        setAccessLogs(response.data.logs || []);
        setAccessLogTotal(response.data.total || 0);
      }
    } catch (error) {
      console.error('접속 로그 조회 실패:', error);
      alert(`접속 로그 조회 실패: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoadingAccessLogs(false);
    }
  };

  // Manager 권한 변경 (특정 부서)
  const handleManagerToggle = async (employeeCode, deptCode, isManager) => {
    try {
      await axios.post(`${API_BASE_URL}/admin/managers/assign`, {
        employee_code: employeeCode,
        dept_code: deptCode,
        is_manager: isManager
      });
      
      // 로컬 상태 업데이트
      setEmployees(prev => prev.map(emp => {
        if (emp.employee_code === employeeCode) {
          const newDeptManagerMap = { ...emp.deptManagerMap, [deptCode]: isManager };
          const isManagerAnywhere = Object.values(newDeptManagerMap).some(v => v === true);
          return {
            ...emp,
            isManager: isManagerAnywhere,
            deptManagerMap: newDeptManagerMap
          };
        }
        return emp;
      }));
    } catch (error) {
      console.error('Manager 권한 변경 실패:', error);
      alert(`Manager 권한 변경 실패: ${error.response?.data?.detail || error.message}`);
    }
  };

  // 편집 모드 진입
  const handleStartEdit = (employeeCode) => {
    console.log('[권한 편집] 편집 모드 진입:', employeeCode);
    const employee = employees.find(emp => emp.employee_code === employeeCode);
    if (employee) {
      console.log('[권한 편집] 직원 정보:', employee);
      setEditingEmployee(employeeCode);
      setEditPermissions({
        isAdmin: employee.isAdmin,
        deptManagerMap: { ...employee.deptManagerMap }
      });
      console.log('[권한 편집] 편집 상태 설정 완료');
    } else {
      console.error('[권한 편집] 직원을 찾을 수 없음:', employeeCode);
    }
  };

  // 편집 취소
  const handleCancelEdit = () => {
    setEditingEmployee(null);
    setEditPermissions({});
  };

  // 편집 중인 권한 변경 (임시 저장)
  const handleEditPermissionChange = (type, value, deptCode = null) => {
    if (type === 'admin') {
      setEditPermissions(prev => ({ ...prev, isAdmin: value }));
    } else if (type === 'manager' && deptCode) {
      setEditPermissions(prev => ({
        ...prev,
        deptManagerMap: {
          ...prev.deptManagerMap,
          [deptCode]: value
        }
      }));
    } else if (type === 'managerAll') {
      // Manager 컬럼 전체 변경: 모든 부서의 Manager 권한을 일괄 변경
      const newDeptManagerMap = {};
      departments.forEach(dept => {
        newDeptManagerMap[dept.dept_code] = value === 'Y';
      });
      setEditPermissions(prev => ({
        ...prev,
        deptManagerMap: newDeptManagerMap
      }));
    }
  };

  // 권한 변경사항 저장
  const handleSavePermissions = async (employeeCode) => {
    try {
      const originalEmployee = employees.find(emp => emp.employee_code === employeeCode);
      if (!originalEmployee) return;

      const changes = [];

      // Admin 권한 변경 확인
      if (editPermissions.isAdmin !== originalEmployee.isAdmin) {
        changes.push({
          type: 'admin',
          value: editPermissions.isAdmin
        });
      }

      // Manager 권한 변경 확인
      const managerChanges = [];
      departments.forEach(dept => {
        const originalValue = originalEmployee.deptManagerMap[dept.dept_code] || false;
        const newValue = editPermissions.deptManagerMap[dept.dept_code] || false;
        if (originalValue !== newValue) {
          managerChanges.push({
            deptCode: dept.dept_code,
            value: newValue
          });
        }
      });

      // 변경사항이 없으면 저장하지 않음
      if (changes.length === 0 && managerChanges.length === 0) {
        handleCancelEdit();
        return;
      }

      // Admin 권한 변경
      if (changes.length > 0 && changes[0].type === 'admin') {
        await handleAdminToggle(employeeCode, changes[0].value);
      }

      // Manager 권한 변경 (순차 처리)
      for (const change of managerChanges) {
        await handleManagerToggle(employeeCode, change.deptCode, change.value);
      }

      // 편집 모드 종료
      handleCancelEdit();
      
      // 성공 메시지
      alert('권한이 성공적으로 변경되었습니다.');
    } catch (error) {
      console.error('권한 저장 실패:', error);
      alert(`권한 저장 실패: ${error.response?.data?.detail || error.message}`);
    }
  };

  // DBConnectionContext의 연결 상태와 단방향 동기화 (무한 루프 방지)
  const syncFromContextRef = React.useRef(false);
  const lastDbContextConnectedRef = React.useRef(dbContextConnected);
  const lastDbContextServerInfoRef = React.useRef(dbContextServerInfo);
  
  useEffect(() => {
    // DBConnectionContext의 상태가 실제로 변경되었을 때만 업데이트
    const contextConnectedChanged = lastDbContextConnectedRef.current !== dbContextConnected;
    const contextServerInfoChanged = lastDbContextServerInfoRef.current !== dbContextServerInfo;
    
    if (contextConnectedChanged && dbContextConnected !== dbConnected && !syncFromContextRef.current) {
      console.log('🔄 [DEBUG] DBConnectionContext 연결 상태 동기화', {
        dbContextConnected,
        dbConnected,
        contextConnectedChanged
      });
      
      syncFromContextRef.current = true;
      lastDbContextConnectedRef.current = dbContextConnected;
      setDbConnected(dbContextConnected);
      
      // 다음 렌더링 사이클에서 플래그 리셋
      setTimeout(() => {
        syncFromContextRef.current = false;
      }, 0);
    }
    
    // 서버 정보가 실제로 변경되었을 때만 업데이트
    if (contextServerInfoChanged && dbContextServerInfo && dbContextServerInfo.database) {
      const serverParts = dbContextServerInfo.server?.split(':') || [];
      const newHost = serverParts[0] || dbConnectionInfo.host;
      const newPort = parseInt(serverParts[1]) || dbConnectionInfo.port;
      const newDatabase = dbContextServerInfo.database || dbConnectionInfo.database;
      const newUsername = dbContextServerInfo.username || dbConnectionInfo.username;
      
      // 실제로 값이 다를 때만 업데이트
      if (newHost !== dbConnectionInfo.host || newPort !== dbConnectionInfo.port || 
          newDatabase !== dbConnectionInfo.database || newUsername !== dbConnectionInfo.username) {
        console.log('🔄 [DEBUG] DBConnectionContext 서버 정보 동기화', {
          newHost,
          newPort,
          newDatabase,
          newUsername
        });
        
        lastDbContextServerInfoRef.current = dbContextServerInfo;
        setDbConnectionInfo(prev => ({
          ...prev,
          host: newHost,
          port: newPort,
          database: newDatabase,
          username: newUsername,
        }));
      }
    }
  }, [dbContextConnected, dbContextServerInfo]); // dbConnected, dbConnectionInfo 제거하여 무한 루프 방지

  // 기준정보 관리, 알람메일 설정, 임시등록 내역 기능 제거
  // 모든 데이터 관리는 DB관리 탭에서 직접 수행

  // DB관리 관련 함수들
  const loadSavedConnectionInfo = async () => {
    try {
      // 백엔드에서 저장된 연결 정보 조회
      try {
        const response = await axios.get(`${API_BASE_URL}/admin/db/config`);
        if (response.data && response.data.host) {
          const connectionInfo = {
            host: response.data.host,
            port: response.data.port,
            database: response.data.database,
            username: response.data.username,
            password: '', // 비밀번호는 백엔드에서 마스킹되어 반환됨
            dbType: 'mysql'
          };
          setDbConnectionInfo(connectionInfo);
          // localStorage에도 동기화 (하위 호환성)
          localStorage.setItem('db_connection_info', JSON.stringify(connectionInfo));
          
          // 초기 상태는 연결 해제 상태로 유지 (자동 연결 시도하지 않음)
          setDbConnected(false);
          localStorage.setItem('db_connected', 'false');
          
          // 저장된 테이블 목록만 복원 (연결은 하지 않음)
          const savedTables = localStorage.getItem('admin_db_tables');
          const savedSelectedTable = localStorage.getItem('admin_selected_table');
          if (savedTables) {
            try {
              const tables = JSON.parse(savedTables);
              setDbTables(tables);
              if (savedSelectedTable && tables.includes(savedSelectedTable)) {
                setSelectedTable(savedSelectedTable);
              }
            } catch (e) {
              console.error('테이블 목록 복원 실패:', e);
            }
          }
        }
      } catch (apiError) {
        // 백엔드 API 실패 시 localStorage에서 로드 (하위 호환성)
        const saved = localStorage.getItem('db_connection_info');
        if (saved) {
          const parsed = JSON.parse(saved);
          setDbConnectionInfo(parsed);
          
          // 초기 상태는 연결 해제 상태로 유지 (자동 연결 시도하지 않음)
          setDbConnected(false);
          localStorage.setItem('db_connected', 'false');
          
          // 저장된 테이블 목록만 복원 (연결은 하지 않음)
          const savedTables = localStorage.getItem('admin_db_tables');
          const savedSelectedTable = localStorage.getItem('admin_selected_table');
          if (savedTables) {
            try {
              const tables = JSON.parse(savedTables);
              setDbTables(tables);
              if (savedSelectedTable && tables.includes(savedSelectedTable)) {
                setSelectedTable(savedSelectedTable);
              }
            } catch (e) {
              console.error('테이블 목록 복원 실패:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('저장된 연결정보 로드 실패:', error);
    }
  };

  const saveConnectionInfo = () => {
    try {
      localStorage.setItem('db_connection_info', JSON.stringify(dbConnectionInfo));
      // 연결 상태도 저장
      localStorage.setItem('db_connected', dbConnected ? 'true' : 'false');
      // 테이블 목록과 선택된 테이블도 저장
      if (dbTables.length > 0) {
        localStorage.setItem('admin_db_tables', JSON.stringify(dbTables));
      }
      if (selectedTable) {
        localStorage.setItem('admin_selected_table', selectedTable);
      }
    } catch (error) {
      console.error('연결정보 저장 실패:', error);
    }
  };

  const handleConnectDB = async () => {
    setIsConnecting(true);
    setConnectionError(null);
    
    try {
      // 필수 필드 검증
      if (!dbConnectionInfo.host || !dbConnectionInfo.database || !dbConnectionInfo.username) {
        setConnectionError('호스트, 데이터베이스, 사용자명은 필수 입력 항목입니다.');
        setIsConnecting(false);
        return;
      }
      
      // 백엔드 API 요구사항에 맞게 데이터 변환
      // DBConnectionInfo 모델: host, port, database, username, password, dbType
      const requestData = {
        host: (dbConnectionInfo.host || '').trim(),
        port: parseInt(dbConnectionInfo.port) || 3306,
        username: (dbConnectionInfo.username || '').trim(),  // 중요: 'username' 필드명 사용
        password: dbConnectionInfo.password || '',
        database: (dbConnectionInfo.database || '').trim(),
        dbType: dbConnectionInfo.dbType || 'mysql'
        // use_ssl은 DBConnectionInfo 모델에 없으므로 제외
      };
      
      // 백엔드 API 호출하여 DB 연결 테스트
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/admin/db/connect`, 
        requestData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token || ''}`
          }
        }
      );
      
      if (response.data.success) {
        setDbConnected(true);
        localStorage.setItem('db_connected', 'true');
        
        // 비밀번호를 sessionStorage에 임시 저장 (연결 후 테이블 목록 조회 등에 사용)
        // 만료 시간: 1시간 (3600000ms) - 세션이 만료되기 전까지 유지
        if (dbConnectionInfo.password) {
          const passwordData = {
            password: dbConnectionInfo.password,
            timestamp: Date.now(),
            expiresIn: 60 * 60 * 1000, // 1시간
            expiresAt: Date.now() + (60 * 60 * 1000)
          };
          sessionStorage.setItem('db_password_temp', JSON.stringify(passwordData));
          console.log('[DB관리] 비밀번호가 세션에 저장되었습니다. 만료 시간: 1시간');
        }
        
        saveConnectionInfo();
        
        // DBConnectionContext에 연결 상태 업데이트 (다른 탭/메뉴로 이동해도 상태 유지)
        const serverDisplay = `${dbConnectionInfo.host}:${dbConnectionInfo.port}`;
        dbContextConnect(serverDisplay, dbConnectionInfo.database, dbConnectionInfo.username);
        
        // 로컬 상태도 업데이트 (즉시 반영)
        setDbConnected(true);
        
        // CustomEvent로도 알림 (다른 컴포넌트와의 호환성)
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('dbConnectionChanged', {
              detail: {
                connected: true,
                server: serverDisplay,
                database: dbConnectionInfo.database,
                username: dbConnectionInfo.username,
              },
            })
          );
          
          // DB 변경 알림 이벤트 (대시보드 등에서 데이터 새로고침용)
          window.dispatchEvent(
            new CustomEvent('dbChanged', {
              detail: {
                host: dbConnectionInfo.host,
                port: dbConnectionInfo.port,
                database: dbConnectionInfo.database,
              },
            })
          );
        }
        
        setConnectionDialogOpen(false);
        // 상태 업데이트 후 테이블 목록 조회
        // dbConnected 상태가 업데이트된 후 실행되도록 약간의 지연 추가
        // 또는 직접 fetchDbTables를 호출하되, 최신 dbConnectionInfo를 사용하도록 함
        setTimeout(() => {
          // executed 플래그 리셋하여 테이블 목록 조회 가능하도록 함
          if (prevDbStateRef.current) {
            prevDbStateRef.current.executed = false;
            prevDbStateRef.current.dbConnected = true;
            prevDbStateRef.current.hasPassword = true;
          }
          
          // fetchDbTables 직접 호출 (ref를 통하지 않고)
          if (fetchDbTablesRef.current) {
            console.log('✅ [DEBUG] handleConnectDB - fetchDbTables 직접 호출');
            fetchDbTablesRef.current();
          } else {
            // ref가 아직 준비되지 않았으면 함수 직접 호출 시도
            console.log('⚠️ [DEBUG] handleConnectDB - fetchDbTablesRef가 없음, 직접 호출 시도');
            // fetchDbTables 함수를 직접 찾아서 호출할 수 없으므로, 
            // useEffect가 자동으로 실행되도록 상태를 업데이트
          }
        }, 200);
      } else {
        setConnectionError(String(response.data.message || 'DB 연결에 실패했습니다.'));
      }
    } catch (error) {
      // 에러 응답을 문자열로 변환
      let errorMessage = 'DB 연결에 실패했습니다.';
      
      if (error.response?.status === 422) {
        // 422 에러: 요청 데이터 검증 실패
        const detail = error.response?.data?.detail;
        if (Array.isArray(detail)) {
          // FastAPI validation error 배열
          errorMessage = detail.map(err => 
            `${err.loc?.join('.')}: ${err.msg}`
          ).join('\n');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (detail?.message) {
          errorMessage = detail.message;
        } else if (typeof detail === 'object') {
          // 객체인 경우 JSON 문자열로 변환
          errorMessage = JSON.stringify(detail);
        } else {
          errorMessage = '입력 데이터 형식이 올바르지 않습니다.';
        }
      } else {
        // 기타 에러
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (detail?.message) {
          errorMessage = detail.message;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => 
            typeof err === 'string' ? err : JSON.stringify(err)
          ).join('\n');
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        } else {
          errorMessage = error.response?.data?.message || error.message || 'DB 연결에 실패했습니다.';
        }
      }
      
      // 문자열로 변환하여 저장
      setConnectionError(String(errorMessage));
      setDbConnected(false);
      localStorage.setItem('db_connected', 'false');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnectDB = () => {
    setDbConnected(false);
    setDbTables([]);
    setSelectedTable(null);
    setTableData([]);
    setTableColumns([]);
    localStorage.setItem('db_connected', 'false');
    // 테이블 관련 저장 정보도 삭제
    localStorage.removeItem('admin_db_tables');
    localStorage.removeItem('admin_selected_table');
    // 임시 저장된 비밀번호도 삭제
    sessionStorage.removeItem('db_password_temp');
    
    // DBConnectionContext에 연결 해제 상태 업데이트 (하단 작업창과 동기화)
    dbContextDisconnect();
    
    // CustomEvent로도 알림 (다른 컴포넌트와의 호환성)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('dbConnectionChanged', {
          detail: { connected: false },
        })
      );
    }
  };

  const fetchDbTables = useCallback(async () => {
    console.log('🚀 [DEBUG] fetchDbTables 호출됨', {
      timestamp: new Date().toISOString(),
      dbConnected,
      dbConnectionInfo: {
        host: dbConnectionInfo.host,
        database: dbConnectionInfo.database,
        username: dbConnectionInfo.username,
        hasPassword: !!dbConnectionInfo.password
      }
    });
    
    // DB 연결 상태 확인
    if (!dbConnected) {
      console.warn('⚠️ [DEBUG] fetchDbTables - DB 연결되지 않음');
      console.log('[DB관리] DB가 연결되지 않아 테이블 목록을 가져오지 않습니다.');
      return [];
    }
    
    // 필수 연결 정보 확인
    if (!dbConnectionInfo.host || !dbConnectionInfo.database || !dbConnectionInfo.username) {
      console.warn('⚠️ [DEBUG] fetchDbTables - 연결 정보 불완전', {
        host: dbConnectionInfo.host,
        database: dbConnectionInfo.database,
        username: dbConnectionInfo.username
      });
      console.log('[DB관리] DB 연결 정보가 불완전하여 테이블 목록을 가져오지 않습니다.');
      return [];
    }
    
    // sessionStorage에서 임시 저장된 비밀번호 가져오기 (만료 여부 확인)
    const tempPassword = getStoredPassword() || dbConnectionInfo.password || '';
    
    console.log('🔍 [DEBUG] fetchDbTables - 비밀번호 확인', {
      hasTempPassword: !!getStoredPassword(),
      hasDbPassword: !!dbConnectionInfo.password,
      passwordLength: tempPassword.length
    });
    
    // 비밀번호가 없으면 에러를 발생시키지 않고 조용히 반환
    if (!tempPassword) {
      console.warn('⚠️ [DEBUG] fetchDbTables - 비밀번호 없음');
      console.log('[DB관리] 비밀번호가 없어 테이블 목록을 가져오지 않습니다. DB 연결 설정에서 비밀번호를 입력하세요.');
      return [];
    }
    
    console.log('✅ [DEBUG] fetchDbTables - API 호출 시작');
    setIsLoadingTables(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const requestData = {
        host: dbConnectionInfo.host || '',
        port: dbConnectionInfo.port || 3306,
        database: dbConnectionInfo.database || '',
        username: dbConnectionInfo.username || '',
        password: tempPassword,
        dbType: dbConnectionInfo.dbType || 'mysql',
      };
      
      // POST 방식으로 변경하여 비밀번호를 body로 전송 (URL 인코딩 문제 방지)
      const response = await axios.post(`${API_BASE_URL}/admin/db/tables`, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const tables = response.data.tables || [];
      
      console.log('✅ [DEBUG] fetchDbTables - API 응답 받음', {
        tablesCount: tables.length,
        tables: tables.slice(0, 5) // 처음 5개만 로그
      });
      
      setDbTables(tables);
      
      // 테이블 목록이 비어있거나 선택된 테이블이 목록에 없으면 선택 해제
      if (tables.length === 0 || (selectedTable && !tables.includes(selectedTable))) {
        console.log('🔍 [DEBUG] fetchDbTables - 선택된 테이블 초기화', {
          tablesLength: tables.length,
          selectedTable
        });
        setSelectedTable(null);
        setTableData([]);
        setTableColumns([]);
      }
      
      // 테이블 목록을 localStorage에 저장
      if (tables.length > 0) {
        localStorage.setItem('admin_db_tables', JSON.stringify(tables));
        console.log('💾 [DEBUG] fetchDbTables - localStorage에 저장됨', {
          tablesCount: tables.length
        });
      }
      
      console.log('✅ [DEBUG] fetchDbTables - 성공적으로 완료');
      return tables;
    } catch (error) {
      console.error('❌ [DEBUG] fetchDbTables - 에러 발생', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });
      console.error('[DB관리] 테이블 목록 조회 실패:', error);
      
      // 테이블 목록 조회 실패 시 선택된 테이블 초기화
      setDbTables([]);
      setSelectedTable(null);
      setTableData([]);
      setTableColumns([]);
      
      // 중복 에러 방지 (5초 이내 동일한 에러는 표시하지 않음)
      const now = Date.now();
      const errorMsg = error.response?.data?.detail || error.message || '알 수 없는 오류';
      
      console.log('🔍 [DEBUG] fetchDbTables - 에러 처리', {
        now,
        lastErrorTime,
        timeDiff: now - lastErrorTime,
        errorMessage,
        errorMsg,
        willShowError: now - lastErrorTime > 5000 || errorMessage !== errorMsg
      });
      
      if (now - lastErrorTime > 5000 || errorMessage !== errorMsg) {
        setErrorMessage(errorMsg);
        setErrorDialogOpen(true);
        setLastErrorTime(now);
      }
      
      return [];
    } finally {
      console.log('🏁 [DEBUG] fetchDbTables - finally 블록 실행 (로딩 종료)');
      setIsLoadingTables(false);
    }
  }, [
    dbConnected, 
    dbConnectionInfo.host, 
    dbConnectionInfo.database, 
    dbConnectionInfo.username, 
    // password는 sessionStorage에서 가져오므로 의존성에서 제거
    dbConnectionInfo.port, 
    dbConnectionInfo.dbType
    // selectedTable, lastErrorTime, errorMessage는 함수 내부에서 사용하지 않으므로 제거
  ]);
  
  // fetchDbTables 함수를 ref에 저장 (useEffect에서 사용하기 위해)
  // 함수가 실제로 변경되었을 때만 로그 출력 (무한 로그 방지)
  const prevFetchDbTablesRef = React.useRef(null);
  const prevDepsKeyRef = React.useRef(null);
  
  // 의존성 키 생성 (핵심 값만 포함)
  const depsKey = `${dbConnected}-${dbConnectionInfo.host}-${dbConnectionInfo.database}-${dbConnectionInfo.username}-${dbConnectionInfo.port}-${dbConnectionInfo.dbType}`;
  
  if (prevFetchDbTablesRef.current !== fetchDbTables || prevDepsKeyRef.current !== depsKey) {
    // 개발 환경에서만 로그 출력
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 [DEBUG] fetchDbTables 함수 업데이트됨', {
        timestamp: new Date().toISOString(),
        fetchDbTablesExists: typeof fetchDbTables === 'function',
        depsChanged: prevDepsKeyRef.current !== depsKey
      });
    }
    prevFetchDbTablesRef.current = fetchDbTables;
    prevDepsKeyRef.current = depsKey;
  }
  fetchDbTablesRef.current = fetchDbTables;

  const fetchTableData = async (tableName) => {
    if (!tableName || !dbConnected) return;
    
    // 필수 연결 정보 확인
    if (!dbConnectionInfo.host || !dbConnectionInfo.database || !dbConnectionInfo.username) {
      console.log('[DB관리] DB 연결 정보가 불완전하여 테이블 데이터를 가져오지 않습니다.');
      return;
    }
    
    // sessionStorage에서 임시 저장된 비밀번호 가져오기 (만료 여부 확인)
    const tempPassword = getStoredPassword() || dbConnectionInfo.password || '';
    
    // 비밀번호가 없으면 에러를 발생시키지 않고 조용히 반환
    if (!tempPassword) {
      console.log('[DB관리] 비밀번호가 없어 테이블 데이터를 가져오지 않습니다.');
      return;
    }
    
    setIsLoadingTableData(true);
    try {
      // password가 빈 문자열이어도 전달되도록 명시적으로 params 구성
      const params = {
        host: dbConnectionInfo.host || '',
        port: dbConnectionInfo.port || 3306,
        database: dbConnectionInfo.database || '',
        username: dbConnectionInfo.username || '',
        password: tempPassword, // sessionStorage에서 가져온 비밀번호 사용
        dbType: dbConnectionInfo.dbType || 'mysql',
      };
      
      const response = await axios.get(`${API_BASE_URL}/admin/db/table/${tableName}`, {
        params: params,
      });
      setTableData(response.data.data || []);
      setTableColumns(response.data.columns || []);
      
      // 컬럼 정보 및 PRIMARY KEY 조회
      try {
        const columnsResponse = await axios.get(`${API_BASE_URL}/admin/db/tables/${tableName}/columns`);
        const columns = columnsResponse.data.columns || [];
        setTableColumnInfo(columns);
        
        // PRIMARY KEY 추출
        const pks = columns.filter(col => col.column_key === 'PRI').map(col => col.column_name);
        setPrimaryKeys(pks);
        console.log('[DB관리] 컬럼 정보 조회 성공:', { columnsCount: columns.length, primaryKeys: pks });
      } catch (error) {
        console.error('[DB관리] 컬럼 정보 조회 실패:', error);
        // 컬럼 정보 조회 실패 시에도 기본값 설정
        setTableColumnInfo([]);
        setPrimaryKeys([]);
      }
    } catch (error) {
      console.error('[DB관리] 테이블 데이터 조회 실패:', error);
      
      // 중복 에러 방지 (5초 이내 동일한 에러는 표시하지 않음)
      const now = Date.now();
      const errorMsg = error.response?.data?.detail || error.message || '테이블 데이터를 가져오는데 실패했습니다.';
      
      if (now - lastErrorTime > 5000 || errorMessage !== errorMsg) {
        setErrorMessage(errorMsg);
        setErrorDialogOpen(true);
        setLastErrorTime(now);
      }
    } finally {
      setIsLoadingTableData(false);
    }
  };

  const handleTableSelect = (tableName) => {
    setSelectedTable(tableName);
    // 선택된 테이블을 localStorage에 저장
    localStorage.setItem('admin_selected_table', tableName);
    // 편집 상태 초기화
    setEditingRowIndex(null);
    setEditingRowData({});
  };

  // 테이블 데이터 조회 함수
  const handleFetchTableData = async () => {
    if (!selectedTable) {
      return;
    }
    await fetchTableData(selectedTable);
  };

  // 테이블 데이터 내보내기 함수
  const handleExportTableData = async () => {
    console.log('🚀 [DEBUG] handleExportTableData 호출됨', {
      selectedTable,
      dbConnected,
      downloadFormat
    });
    
    if (!selectedTable || !dbConnected) {
      console.warn('⚠️ [DEBUG] handleExportTableData - 조건 불만족', {
        selectedTable,
        dbConnected
      });
      return;
    }
    
    // 필수 연결 정보 확인
    if (!dbConnectionInfo.host || !dbConnectionInfo.database || !dbConnectionInfo.username) {
      console.warn('⚠️ [DEBUG] handleExportTableData - DB 연결 정보 불완전', {
        host: dbConnectionInfo.host,
        database: dbConnectionInfo.database,
        username: dbConnectionInfo.username
      });
      console.log('[DB관리] DB 연결 정보가 불완전하여 데이터를 내보낼 수 없습니다.');
      return;
    }
    
    // sessionStorage에서 임시 저장된 비밀번호 가져오기
    const tempPassword = getStoredPassword() || dbConnectionInfo.password || '';
    
    if (!tempPassword) {
      console.warn('⚠️ [DEBUG] handleExportTableData - 비밀번호 없음');
      console.log('[DB관리] 비밀번호가 없어 데이터를 내보낼 수 없습니다.');
      return;
    }
    
    setIsExporting(true);
    try {
      const params = {
        host: dbConnectionInfo.host || '',
        port: dbConnectionInfo.port || 3306,
        database: dbConnectionInfo.database || '',
        username: dbConnectionInfo.username || '',
        password: tempPassword,
        dbType: dbConnectionInfo.dbType || 'mysql',
        format: downloadFormat,
      };
      
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const url = `${API_BASE_URL}/admin/db/export/${selectedTable}`;
      
      console.log('📡 [DEBUG] Export API 호출 시작', {
        url,
        params: { ...params, password: '***' }, // 비밀번호는 마스킹
        format: downloadFormat,
        hasToken: !!token
      });
      
      const response = await axios.get(
        url,
        {
          params: params,
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('✅ [DEBUG] Export API 응답 받음', {
        status: response.status,
        statusText: response.statusText,
        headers: {
          'content-type': response.headers['content-type'],
          'content-disposition': response.headers['content-disposition'],
          'content-length': response.headers['content-length']
        },
        dataSize: response.data?.size || 'unknown'
      });
      
      // Content-Disposition 헤더에서 파일명 추출
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `${selectedTable}_${new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19).replace('T', '_')}.${downloadFormat}`;
      
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
          // UTF-8 인코딩된 파일명 처리
          if (fileName.startsWith("UTF-8''")) {
            fileName = decodeURIComponent(fileName.substring(7));
          }
        }
      }
      
      console.log('📥 [DEBUG] 파일명 추출 완료', { fileName });
      
      // Blob 생성 및 다운로드
      const blob = new Blob([response.data], {
        type: downloadFormat === 'csv' 
          ? 'text/csv;charset=utf-8;' 
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      console.log('💾 [DEBUG] Blob 생성 완료', {
        blobSize: blob.size,
        blobType: blob.type
      });
      
      const url_obj = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url_obj;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url_obj);
      
      console.log('✅ [DB관리] 데이터 내보내기 성공:', fileName);
    } catch (error) {
      console.error('❌ [DEBUG] Export API 호출 실패 - 상세 정보', {
        error: {
          message: error.message,
          name: error.name,
          code: error.code,
          response: error.response ? {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data,
            headers: error.response.headers
          } : null,
          request: error.request ? {
            url: error.config?.url,
            method: error.config?.method,
            params: error.config?.params ? { ...error.config.params, password: '***' } : null,
            headers: error.config?.headers
          } : null,
          config: error.config ? {
            url: error.config.url,
            method: error.config.method,
            baseURL: error.config.baseURL,
            timeout: error.config.timeout
          } : null
        },
        stack: error.stack
      });
      
      const errorMsg = error.response?.data?.detail || error.response?.statusText || error.message || '데이터 내보내기에 실패했습니다.';
      const fullErrorMsg = error.response 
        ? `[${error.response.status}] ${errorMsg}`
        : errorMsg;
      
      console.error('[DB관리] 데이터 내보내기 실패:', fullErrorMsg);
      setErrorMessage(fullErrorMsg);
      setErrorDialogOpen(true);
    } finally {
      setIsExporting(false);
      console.log('🏁 [DEBUG] handleExportTableData 완료');
    }
  };

  // V30: 통합 투입시간 리포트 다운로드 (직원명·고객사명·계약명 포함)
  const downloadIntegratedReport = async () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) {
      setErrorMessage('로그인이 필요합니다.');
      setErrorDialogOpen(true);
      return;
    }
    setIsExportingIntegrated(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/reports/integrated-timeinput`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data || [];
      const excelData = data.map((row) => ({
        '날짜': row.log_date ?? '',
        '직원코드': row.employee_code ?? '',
        '직원명': row.employee_name ?? '',
        '부서': row.department ?? '',
        '고객사코드': row.company_code ?? '',
        '고객사명': row.company_name_kr ?? '',
        '계약ID': row.contract_id ?? '',
        '계약명': row.contract_title ?? '',
        '업무구분': row.job_category ?? '',
        '상세내용': row.input_description ?? '',
        '분(Min)': row.input_minute ?? '',
        '시간(Hour)': row.input_hours ?? '',
      }));
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '통합리포트');
      const fileName = `Integrated_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || '리포트 다운로드 중 오류가 발생했습니다.';
      setErrorMessage(msg);
      setErrorDialogOpen(true);
    } finally {
      setIsExportingIntegrated(false);
    }
  };

  // 행 추가 시작
  const handleAddRow = () => {
    setIsAddingRow(true);
    setEditingRowIndex(null);
    setEditingRowData({});
    // 새 행 데이터 초기화 (필수 필드만 포함)
    const initialData = {};
    tableColumnInfo.forEach(col => {
      if (col.column_default !== null && col.column_default !== undefined) {
        initialData[col.column_name] = col.column_default;
      } else if (col.is_nullable === 'YES') {
        initialData[col.column_name] = null;
      }
    });
    setNewRowData(initialData);
  };

  // 새 행 데이터 변경
  const handleNewRowChange = (columnName, value) => {
    setNewRowData(prev => ({ ...prev, [columnName]: value }));
  };

  // 새 행 저장
  const handleSaveNewRow = async () => {
    if (!selectedTable) return;
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/admin/db/tables/${selectedTable}/row`,
        newRowData
      );
      
      if (response.data.success) {
        alert('행이 추가되었습니다.');
        setIsAddingRow(false);
        setNewRowData({});
        fetchTableData(selectedTable);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || '행 추가 실패';
      alert(errorMsg);
    }
  };

  // 새 행 추가 취소
  const handleCancelNewRow = () => {
    setIsAddingRow(false);
    setNewRowData({});
  };

  // 행 편집 시작
  const handleEditRow = (index) => {
    setEditingRowIndex(index);
    setEditingRowData({ ...tableData[index] });
    setIsAddingRow(false);
  };

  // 편집 중인 행 데이터 변경
  const handleEditRowChange = (columnName, value) => {
    setEditingRowData(prev => ({ ...prev, [columnName]: value }));
  };

  // 행 수정 저장
  const handleSaveEditRow = async () => {
    if (!selectedTable || editingRowIndex === null) return;
    
    try {
      const response = await axios.put(
        `${API_BASE_URL}/admin/db/tables/${selectedTable}/row`,
        editingRowData
      );
      
      if (response.data.success) {
        alert('행이 수정되었습니다.');
        setEditingRowIndex(null);
        setEditingRowData({});
        fetchTableData(selectedTable);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || '행 수정 실패';
      alert(errorMsg);
    }
  };

  // 행 편집 취소
  const handleCancelEditRow = () => {
    setEditingRowIndex(null);
    setEditingRowData({});
  };

  // 행 삭제
  const handleDeleteRow = (row) => {
    setRowToDelete(row);
    setDeleteConfirmMessage('이 행을 삭제하시겠습니까?');
    setDeleteConfirmCallback(() => async () => {
      if (!selectedTable || !rowToDelete) return;
      
      try {
        // PRIMARY KEY 데이터 추출
        const primaryKeyData = {};
        primaryKeys.forEach(pk => {
          if (rowToDelete[pk] !== undefined && rowToDelete[pk] !== null) {
            primaryKeyData[pk] = rowToDelete[pk];
          }
        });
        
        const response = await axios.delete(
          `${API_BASE_URL}/admin/db/tables/${selectedTable}/row`,
          { data: primaryKeyData }
        );
        
        if (response.data.success) {
          alert('행이 삭제되었습니다.');
          setRowToDelete(null);
          fetchTableData(selectedTable);
        }
      } catch (error) {
        const errorMsg = error.response?.data?.detail || error.message || '행 삭제 실패';
        alert(errorMsg);
      }
      setDeleteConfirmOpen(false);
    });
    setDeleteConfirmOpen(true);
  };

  // 삭제 확인 모달 핸들러
  const handleDeleteConfirm = () => {
    if (deleteConfirmCallback) {
      deleteConfirmCallback();
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDeleteConfirmCallback(null);
    setDeleteConfirmMessage('');
    setRowToDelete(null);
  };

  const handleDownloadTableTemplate = () => {
    if (!selectedTable || tableColumns.length === 0) {
      alert('테이블을 선택하고 조회한 후 양식을 다운로드할 수 있습니다.');
      return;
    }
    
    const columns = tableColumns.map(col => ({
      key: col.name,
      label: col.name,
      width: 15,
    }));
    
    downloadExcelTemplate(columns, `${selectedTable}_양식.xlsx`);
  };

  const handleDownloadAllTemplates = async () => {
    if (!dbConnected) {
      alert('DB에 연결되어 있어야 합니다.');
      return;
    }
    
    // 테이블 목록이 없으면 먼저 조회 시도
    let tables = dbTables;
    if (tables.length === 0) {
      tables = await fetchDbTables();
      if (!tables || tables.length === 0) {
        alert('테이블이 없거나 조회할 수 없습니다.');
        return;
      }
    }
    
    try {
      // getStoredPassword()를 사용하여 만료 여부 확인된 비밀번호 가져오기
      const tempPassword = getStoredPassword() || dbConnectionInfo.password || '';
      
      if (!tempPassword) {
        alert('DB 비밀번호가 없거나 만료되었습니다. DB 연결 설정에서 비밀번호를 다시 입력해주세요.');
        return;
      }
      
      const params = new URLSearchParams({
        host: dbConnectionInfo.host,
        port: dbConnectionInfo.port.toString(),
        database: dbConnectionInfo.database,
        username: dbConnectionInfo.username,
        password: tempPassword, // 만료 확인된 비밀번호 사용
        dbType: dbConnectionInfo.dbType,
      }).toString();
      const url = `${API_BASE_URL}/admin/db/download-all-templates?${params}`;
      
      // 파일 다운로드
      const response = await axios.get(url, {
        responseType: 'blob',
      });
      
      // Blob을 파일로 다운로드
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${dbConnectionInfo.database}_모든테이블_양식.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      alert(`${tables.length}개 테이블의 양식이 다운로드되었습니다.`);
    } catch (error) {
      console.error('양식 일괄 다운로드 실패:', error);
      
      // 에러 메시지 개선
      let errorMessage = error.response?.data?.detail || error.message || '알 수 없는 오류가 발생했습니다.';
      
      // DB 인증 실패인 경우 명확한 메시지 제공
      if (errorMessage.includes('Access denied') || errorMessage.includes('1045')) {
        errorMessage = 'DB 비밀번호가 잘못되었거나 만료되었습니다.\n\nDB 연결 설정에서 비밀번호를 다시 입력하고 연결을 재시도해주세요.';
      } else if (errorMessage.includes('Connection') || errorMessage.includes('연결')) {
        errorMessage = 'DB 연결에 실패했습니다.\n\nDB 연결 설정을 확인하고 다시 시도해주세요.';
      }
      
      alert(`양식 일괄 다운로드 실패:\n\n${errorMessage}`);
    }
  };

  const handleUploadTableData = async (file) => {
    if (!selectedTable || !dbConnected) {
      alert('테이블을 선택하고 DB에 연결되어 있어야 합니다.');
      return;
    }
    
    try {
      const data = await readExcelFile(file);
      if (data && data.length > 0) {
        // FormData로 파일과 테이블 정보 전송
        const formData = new FormData();
        formData.append('file', file);
        formData.append('table', selectedTable);
        formData.append('connection_info', JSON.stringify(dbConnectionInfo));
        
        const response = await axios.post(`${API_BASE_URL}/admin/db/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        if (response.data.success) {
          alert(`${response.data.inserted_count || data.length}개의 데이터가 업로드되었습니다.`);
          // 테이블 데이터 다시 조회
          fetchTableData(selectedTable);
          
          // 데이터 변경 이벤트 발생 (EmployeeTreeSelect 자동 업데이트)
          if (selectedTable === 'dept') {
            notifyDeptChanged('update', { count: response.data.inserted_count || data.length });
          } else if (selectedTable === 'employee') {
            notifyEmployeeChanged('update', { count: response.data.inserted_count || data.length });
          } else if (selectedTable === 'dept_employee_link') {
            notifyDeptEmployeeLinkChanged('update', { count: response.data.inserted_count || data.length });
          }
        } else {
          alert(`업로드 실패: ${response.data.message || '알 수 없는 오류'}`);
        }
      }
    } catch (error) {
      alert(`파일 업로드 실패: ${error.response?.data?.detail || error.message}`);
    }
  };

  // 파일 선택 시 미리보기 표시
  const handleFileSelectForPreview = async (file) => {
    if (!dbConnected) {
      alert('DB에 연결되어 있어야 합니다.');
      return;
    }
    
    try {
      // 엑셀 파일의 모든 시트 읽기
      const sheetData = await readExcelFileAllSheets(file);
      
      // 빈 시트 필터링
      const filteredData = {};
      Object.keys(sheetData).forEach(sheetName => {
        if (sheetData[sheetName] && sheetData[sheetName].length > 0) {
          filteredData[sheetName] = sheetData[sheetName];
        }
      });
      
      if (Object.keys(filteredData).length === 0) {
        alert('읽을 수 있는 데이터가 없습니다. 엑셀 파일에 데이터가 있는지 확인하세요.');
        return;
      }
      
      // 미리보기 데이터 설정 및 다이얼로그 열기
      setUploadPreviewData(filteredData);
      setPendingUploadFile(file); // 나중에 업로드할 파일 저장
      setSelectedPreviewTable(Object.keys(filteredData)[0]); // 첫 번째 테이블 선택
      // 모든 테이블을 기본 선택 상태로 설정
      setSelectedTablesForUpload(new Set(Object.keys(filteredData)));
      setUploadPreviewDialogOpen(true);
    } catch (error) {
      alert(`파일 읽기 실패: ${error.message}`);
    }
  };
  
  // 선택된 테이블만 포함하는 엑셀 파일 생성
  const createExcelFileWithSelectedTables = (selectedTableNames) => {
    if (!uploadPreviewData || selectedTableNames.size === 0) {
      return null;
    }
    
    const wb = XLSX.utils.book_new();
    
    // 선택된 테이블만 시트로 추가
    selectedTableNames.forEach(tableName => {
      if (uploadPreviewData[tableName] && uploadPreviewData[tableName].length > 0) {
        const ws = XLSX.utils.json_to_sheet(uploadPreviewData[tableName]);
        XLSX.utils.book_append_sheet(wb, ws, tableName);
      }
    });
    
    // 워크북을 Blob으로 변환
    const wbout = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    
    // File 객체 생성
    const fileName = pendingUploadFile?.name || 'upload.xlsx';
    return new File([blob], fileName, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  };

  // 선택된 테이블만 업로드 실행
  const handleUploadSelectedTables = async () => {
    if (!pendingUploadFile) {
      alert('업로드할 파일이 없습니다.');
      return;
    }
    
    if (!dbConnected) {
      alert('DB에 연결되어 있어야 합니다.');
      return;
    }
    
    if (selectedTablesForUpload.size === 0) {
      alert('업로드할 테이블을 최소 1개 이상 선택해주세요.');
      return;
    }
    
    setIsUploadingAll(true);
    setUploadAllResults(null);
    // 미리보기 다이얼로그는 닫지 않고 유지 (다른 테이블도 업로드 가능하도록)
    
    try {
      // 선택된 테이블만 포함하는 엑셀 파일 생성
      const selectedFile = createExcelFileWithSelectedTables(selectedTablesForUpload);
      
      if (!selectedFile) {
        alert('선택된 테이블로 파일을 생성할 수 없습니다.');
        setIsUploadingAll(false);
        return;
      }
      
      // sessionStorage에서 임시 저장된 비밀번호 가져오기 (없으면 현재 비밀번호 사용)
      const tempPassword = sessionStorage.getItem('db_password_temp') || dbConnectionInfo.password || '';
      
      // connection_info에 비밀번호 포함
      const connectionInfoWithPassword = {
        ...dbConnectionInfo,
        password: tempPassword
      };
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('connection_info', JSON.stringify(connectionInfoWithPassword));
      
      const response = await axios.post(`${API_BASE_URL}/admin/db/upload-all`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        setUploadAllResults(response.data);
        setUploadAllDialogOpen(true);
        // 업로드 성공 후에도 미리보기 데이터 유지 (다른 테이블도 업로드 가능하도록)
        // 선택된 테이블만 제거하고 나머지는 유지
        const remainingTables = new Set(selectedTablesForUpload);
        selectedTablesForUpload.forEach(tableName => {
          remainingTables.delete(tableName);
        });
        setSelectedTablesForUpload(remainingTables);
        
        // DB 연결 상태 명시적으로 유지 (업로드 후에도 연결 유지)
        // useEffect가 자동으로 DBConnectionContext와 동기화함
        setDbConnected(true);
        localStorage.setItem('db_connected', 'true');
        
        // 데이터 변경 이벤트 발생 (EmployeeTreeSelect 자동 업데이트)
        const uploadedTables = Array.from(selectedTablesForUpload);
        if (uploadedTables.includes('dept')) {
          notifyDeptChanged('update', { count: response.data.results?.dept?.inserted_count || 0 });
        }
        if (uploadedTables.includes('employee')) {
          notifyEmployeeChanged('update', { count: response.data.results?.employee?.inserted_count || 0 });
        }
        if (uploadedTables.includes('dept_employee_link')) {
          notifyDeptEmployeeLinkChanged('update', { count: response.data.results?.dept_employee_link?.inserted_count || 0 });
        }
        
        // 업로드 성공 후 테이블 목록 새로고침 및 저장
        setTimeout(async () => {
          const tables = await fetchDbTables();
          if (tables && tables.length > 0) {
            localStorage.setItem('admin_db_tables', JSON.stringify(tables));
          }
        }, 500);
      } else {
        alert(`업로드 실패: ${response.data.message || '알 수 없는 오류'}`);
        // 실패 시에도 DB 연결 상태 유지
        if (dbConnectionInfo.database) {
          setDbConnected(true);
          localStorage.setItem('db_connected', 'true');
        }
      }
    } catch (error) {
      // 업로드 실패 시에도 DB 연결 상태 유지
      if (dbConnectionInfo.database) {
        setDbConnected(true);
        localStorage.setItem('db_connected', 'true');
      }
      alert(`업로드 실패: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsUploadingAll(false);
    }
  };

  // 모든 테이블 일괄 업로드 실행
  const handleUploadAllTables = async () => {
    if (!pendingUploadFile) {
      alert('업로드할 파일이 없습니다.');
      return;
    }
    
    if (!dbConnected) {
      alert('DB에 연결되어 있어야 합니다.');
      return;
    }
    
    setIsUploadingAll(true);
    setUploadAllResults(null);
    // 일괄 업로드 시에는 미리보기 다이얼로그 닫기 (모든 테이블 업로드 완료)
    setUploadPreviewDialogOpen(false);
    
    try {
      // sessionStorage에서 임시 저장된 비밀번호 가져오기 (없으면 현재 비밀번호 사용)
      const tempPassword = sessionStorage.getItem('db_password_temp') || dbConnectionInfo.password || '';
      
      // connection_info에 비밀번호 포함
      const connectionInfoWithPassword = {
        ...dbConnectionInfo,
        password: tempPassword
      };
      
      const formData = new FormData();
      formData.append('file', pendingUploadFile);
      formData.append('connection_info', JSON.stringify(connectionInfoWithPassword));
      
      const response = await axios.post(`${API_BASE_URL}/admin/db/upload-all`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        setUploadAllResults(response.data);
        setUploadAllDialogOpen(true);
        // 일괄 업로드 성공 후 미리보기 데이터 초기화 (모든 테이블 업로드 완료)
        setUploadPreviewData(null);
        setPendingUploadFile(null);
        setSelectedTablesForUpload(new Set());
        
        // DB 연결 상태 명시적으로 유지 (업로드 후에도 연결 유지)
        // useEffect가 자동으로 DBConnectionContext와 동기화함
        setDbConnected(true);
        localStorage.setItem('db_connected', 'true');
        
        // 데이터 변경 이벤트 발생 (EmployeeTreeSelect 자동 업데이트)
        // response.data.results에 각 테이블별 결과가 있을 수 있음
        if (response.data.results) {
          if (response.data.results.dept) {
            notifyDeptChanged('update', { count: response.data.results.dept.inserted_count || 0 });
          }
          if (response.data.results.employee) {
            notifyEmployeeChanged('update', { count: response.data.results.employee.inserted_count || 0 });
          }
          if (response.data.results.dept_employee_link) {
            notifyDeptEmployeeLinkChanged('update', { count: response.data.results.dept_employee_link.inserted_count || 0 });
          }
        } else {
          // results가 없으면 모든 테이블 변경으로 간주
          notifyDeptChanged('update');
          notifyEmployeeChanged('update');
          notifyDeptEmployeeLinkChanged('update');
        }
        
        // 업로드 성공 후 테이블 목록 새로고침 및 저장
        setTimeout(async () => {
          const tables = await fetchDbTables();
          if (tables && tables.length > 0) {
            localStorage.setItem('admin_db_tables', JSON.stringify(tables));
          }
        }, 500);
      } else {
        alert(`일괄 업로드 실패: ${response.data.message || '알 수 없는 오류'}`);
        // 실패 시에도 DB 연결 상태 유지
        if (dbConnectionInfo.database) {
          setDbConnected(true);
          localStorage.setItem('db_connected', 'true');
        }
      }
    } catch (error) {
      // 업로드 실패 시에도 DB 연결 상태 유지
      if (dbConnectionInfo.database) {
        setDbConnected(true);
        localStorage.setItem('db_connected', 'true');
      }
      alert(`일괄 업로드 실패: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsUploadingAll(false);
    }
  };

  // 탭 관련 함수 제거 (DB관리만 존재)

  // handleCellClick는 현재 사용되지 않음
  // const handleCellClick = (rowId, field, value) => {
  //   setEditingCell({ rowId, field });
  //   setEditValue(value || '');
  // };

  // 기준정보 관리 관련 함수 제거 (DB관리에서 직접 관리)

  // 기준정보 관리 관련 함수 제거 (DB관리에서 직접 관리)

  // 기준정보 관리 관련 함수 제거 (DB관리에서 직접 관리)

  // 기준정보 관리 관련 함수 제거 (DB관리에서 직접 관리)

  // 다음 함수들은 현재 사용되지 않음
  // const handleColumnWidthChange = useCallback((width, columnKey, dataType) => {
  //   setColumnWidths(prev => ({
  //     ...prev,
  //     [dataType]: { ...prev[dataType], [columnKey]: width }
  //   }));
  // }, []);

  // const [rowHeights, setRowHeights] = useState({});
  // const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = React.useRef(null);

  // const handleRowHeightChange = useCallback((height, rowId, dataType) => {
  //   setRowHeights(prev => ({
  //     ...prev,
  //     [dataType]: { ...prev[dataType], [rowId]: height }
  //   }));
  // }, []);

  // 기준정보 관리 및 임시등록 내역 파일 업로드 함수 제거 (DB관리에서 직접 관리)

  // 기준정보 관리 및 임시등록 내역 양식 다운로드 함수 제거 (DB관리에서 직접 관리)

  // 기준정보 관리 및 임시등록 내역 파일 핸들러 제거 (DB관리에서 직접 관리)

  // 기준정보 관리 및 임시등록 내역 렌더링 함수 제거 (DB관리에서 직접 관리)

  // 모바일 뷰 경고 다이얼로그
  const MobileWarningDialog = () => (
    <Dialog
      open={mobileWarningDialogOpen}
      onClose={() => setMobileWarningDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setMobileWarningDialogOpen(false);
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AlertTriangle size={24} style={{ color: colors.warning }} />
          <Typography variant="h6">모바일 환경 경고</Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
              Raw 데이터 모드입니다
            </Typography>
            <Typography variant="body2">
              이 기능은 PC 환경에 최적화되어 있습니다. 모바일에서는 일부 기능이 제한될 수 있으며,
              가로 스크롤이 필요할 수 있습니다.
            </Typography>
          </Alert>
          <Typography variant="body2" sx={{ color: colors.secondaryText }}>
            긴급한 데이터 수정이 필요한 경우에만 모바일에서 사용하시기 바랍니다.
            일반적인 관리 작업은 PC 환경에서 진행하는 것을 권장합니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setMobileWarningDialogOpen(false)} variant="outlined">
            취소
          </Button>
          <Button 
            type="submit"
            variant="contained"
            sx={{ backgroundColor: colors.accent }}
          >
            계속 진행
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );

  const notificationPermissionLabel =
    notificationPermission === 'granted'
      ? '허용'
      : notificationPermission === 'denied'
        ? '거부'
        : notificationPermission === 'default'
          ? '미설정'
          : '미지원';

  return (
    <Box sx={{ p: 1.5, pt: 3, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {/* 모바일 경고 다이얼로그 */}
      <MobileWarningDialog />
      
      {/* 모바일 뷰: 가로 스크롤 안내 */}
      {isMobileView && (
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
          icon={<AlertTriangle size={20} />}
        >
          <Typography variant="body2">
            💡 테이블이 넓을 경우 가로 스크롤을 사용하세요. 화면을 좌우로 스와이프하여 모든 컬럼을 확인할 수 있습니다.
          </Typography>
        </Alert>
      )}
      
      <Typography variant="h5" sx={{ color: colors.primaryText, fontWeight: 600, mb: 2 }}>
        관리자 페이지
      </Typography>

      <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0, minHeight: 0, overflow: 'hidden', '&:last-child': { pb: 0 } }}>
          {/* 탭 메뉴 */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1, flexShrink: 0 }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab 
                icon={<Database size={18} />} 
                iconPosition="start"
                label="DB관리" 
                sx={{ textTransform: 'none', minHeight: 48 }}
              />
              <Tab 
                icon={<Shield size={18} />} 
                iconPosition="start"
                label="권한 관리" 
                sx={{ textTransform: 'none', minHeight: 48 }}
              />
              <Tab 
                icon={<FileText size={18} />} 
                iconPosition="start"
                label="접속 로그" 
                sx={{ textTransform: 'none', minHeight: 48 }}
              />
              <Tab 
                icon={<Bell size={18} />} 
                iconPosition="start"
                label="알림 관리" 
                sx={{ textTransform: 'none', minHeight: 48 }}
              />
            </Tabs>
          </Box>
          
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 1.5, minHeight: 0 }}>
            {/* DB관리 탭 */}
            {tabValue === 0 && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', minHeight: 0, overflow: 'hidden' }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="h6">DB관리</Typography>
                  {dbConnected && (
                    <>
                      <Tooltip title="직원명·고객사명·계약명이 포함된 통합 투입시간 리포트(XLSX)">
                        <span>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={downloadIntegratedReport}
                            disabled={isExportingIntegrated}
                            sx={{
                              backgroundColor: '#28a745',
                              color: 'white',
                              marginRight: 1,
                              '&:hover': { backgroundColor: '#218838' },
                            }}
                          >
                            {isExportingIntegrated ? '받는 중...' : '📊 통합 리포트(XLSX)'}
                          </Button>
                        </span>
                      </Tooltip>
                      <Button
                        size="small"
                        variant="text"
                        onClick={handleDownloadAllTemplates}
                        disabled={isLoadingTables}
                        sx={{
                          color: '#1976d2',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                            backgroundColor: 'transparent',
                            color: '#1565c0',
                          },
                          '&:disabled': {
                            color: 'rgba(0, 0, 0, 0.26)',
                          },
                          minWidth: 'auto',
                          padding: '4px 8px',
                          fontSize: '14px',
                        }}
                      >
                        양식 일괄 다운로드
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = '.xls,.xlsx,.xlsb,.xlsm';
                          input.onchange = (e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileSelectForPreview(file);
                          };
                          input.click();
                        }}
                        disabled={isUploadingAll || isLoadingTables}
                        sx={{
                          color: '#1976d2',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                            backgroundColor: 'transparent',
                            color: '#1565c0',
                          },
                          '&:disabled': {
                            color: 'rgba(0, 0, 0, 0.26)',
                          },
                          minWidth: 'auto',
                          padding: '4px 8px',
                          fontSize: '14px',
                        }}
                      >
                        {isUploadingAll ? '업로드 중...' : '양식 일괄 업로드'}
                      </Button>
                      {selectedTable && (
                        <>
                          <Button
                            size="small"
                            variant="text"
                            onClick={handleDownloadTableTemplate}
                            sx={{
                              color: '#1976d2',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                                backgroundColor: 'transparent',
                                color: '#1565c0',
                              },
                              minWidth: 'auto',
                              padding: '4px 8px',
                              fontSize: '14px',
                            }}
                          >
                            양식 다운로드
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                              color: '#1976d2',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                                backgroundColor: 'transparent',
                                color: '#1565c0',
                              },
                              minWidth: 'auto',
                              padding: '4px 8px',
                              fontSize: '14px',
                            }}
                          >
                            데이터 업로드
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </Box>
                {dbConnected && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {/* 테이블 선택 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap', color: colors.primaryText }}>
                        테이블 선택:
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                          value={selectedTable || ''}
                          onChange={(e) => handleTableSelect(e.target.value)}
                          disabled={isLoadingTables}
                          displayEmpty
                        >
                        {isLoadingTables ? (
                          <MenuItem disabled value="">
                            <em>테이블 목록 로딩 중...</em>
                          </MenuItem>
                        ) : dbTables.length === 0 ? (
                          <MenuItem disabled value="">
                            <em>테이블이 없습니다</em>
                          </MenuItem>
                        ) : (
                          dbTables.map((table) => (
                            <MenuItem key={table} value={table}>
                              {table}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                    </Box>
                    {/* 조회 버튼 */}
                    {selectedTable && (
                      <Button 
                        size="small" 
                        variant="contained"
                        onClick={handleFetchTableData}
                        disabled={isLoadingTableData}
                        sx={{ backgroundColor: colors.primary }}
                      >
                        {isLoadingTableData ? '조회 중...' : '조회'}
                      </Button>
                    )}
                    {/* 다운로드 형식 선택 및 다운로드 버튼 */}
                    {selectedTable && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                            value={downloadFormat}
                            onChange={(e) => setDownloadFormat(e.target.value)}
                            disabled={isExporting}
                          >
                            <MenuItem value="xlsx">XLSX</MenuItem>
                            <MenuItem value="csv">CSV</MenuItem>
                          </Select>
                        </FormControl>
                        <Button 
                          size="small" 
                          variant="contained"
                          startIcon={<Download size={16} />}
                          onClick={handleExportTableData}
                          disabled={isExporting || !selectedTable}
                          sx={{ backgroundColor: colors.accent }}
                        >
                          {isExporting ? '다운로드 중...' : '다운로드'}
                        </Button>
                      </Box>
                    )}
                    {/* 행 추가 버튼 */}
                    {selectedTable && !isAddingRow && (
                      <Button 
                        size="small" 
                        variant="contained"
                        startIcon={<Plus size={16} />} 
                        onClick={handleAddRow}
                        sx={{ backgroundColor: colors.accent }}
                      >
                        행 추가
                      </Button>
                    )}
                    <Button size="small" variant="outlined" onClick={handleDisconnectDB}>
                      연결 해제
                    </Button>
                  </Box>
                )}
              </Box>

              {dbConnected ? (
                  <>
                    {!isLoadingTables && dbTables.length === 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          데이터베이스에 테이블이 없거나 조회 권한이 없을 수 있습니다.
                        </Typography>
                      </Box>
                    )}

                    {selectedTable && (
                      <Box sx={{ 
                        flex: 1, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        minHeight: 0, 
                        overflow: 'hidden',
                      }}>
                        {/* 행 입력 섹션 - JobTimeInput 스타일 */}
                        {isAddingRow && (
                          <Card sx={{ mb: 2, backgroundColor: isDark ? '#1C1D22' : '#F8F9FA', border: `1px solid ${colors.primaryBorder}`, flexShrink: 0 }}>
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Plus size={18} style={{ color: colors.accent }} />
                                  <Typography variant="h6" sx={{ color: colors.primaryText, fontWeight: 600 }}>
                                    행 추가
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    variant="contained"
                                    startIcon={<Save size={16} />}
                                    onClick={handleSaveNewRow}
                                    sx={{
                                      backgroundColor: colors.accent,
                                      minWidth: 100,
                                      height: '32px',
                                      fontSize: '14px',
                                      fontWeight: 600,
                                    }}
                                  >
                                    저장
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    startIcon={<X size={16} />}
                                    onClick={handleCancelNewRow}
                                    sx={{
                                      borderColor: colors.secondaryText,
                                      color: colors.secondaryText,
                                      height: '32px',
                                    }}
                                  >
                                    취소
                                  </Button>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                {tableColumns.map((col) => {
                                  const colInfo = tableColumnInfo.find(c => c.column_name === col.name);
                                  const isAutoIncrement = colInfo?.extra?.toLowerCase().includes('auto_increment') || false;
                                  const isPrimaryKey = primaryKeys.includes(col.name);
                                  
                                  if (isAutoIncrement) {
                                    return null; // Auto increment 컬럼은 표시하지 않음
                                  }
                                  
                                  return (
                                    <TextField
                                      key={col.name}
                                      label={col.name}
                                      size="small"
                                      value={newRowData[col.name] !== null && newRowData[col.name] !== undefined ? String(newRowData[col.name]) : ''}
                                      onChange={(e) => handleNewRowChange(col.name, e.target.value)}
                                      placeholder={colInfo?.is_nullable === 'YES' ? '선택' : '필수'}
                                      required={colInfo?.is_nullable === 'NO' && !isPrimaryKey}
                                      sx={{ 
                                        minWidth: 200,
                                        flex: '1 1 200px',
                                        '& .MuiInputBase-root': {
                                          fontSize: '14px',
                                        }
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            </CardContent>
                          </Card>
                        )}

                        {/* 액션 버튼들 */}
                        <Box sx={{ mb: 1, display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xls,.xlsx,.xlsb,.xlsm,.csv"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleUploadTableData(file);
                              if (fileInputRef.current) fileInputRef.current.value = '';
                            }}
                          />
                        </Box>

                        {/* 테이블 데이터 표시 */}
                        {isLoadingTableData ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flexShrink: 0 }}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <Box sx={{ 
                            flex: 1, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            minHeight: 0, 
                            overflow: 'hidden', 
                            border: `1px solid ${isDark ? '#555' : '#ddd'}`, 
                            borderRadius: 1,
                          }}>
                            <TableContainer 
                              sx={{ 
                                flex: 1, 
                                overflow: 'auto', 
                                width: '100%',
                                minHeight: 0,
                                '&::-webkit-scrollbar': {
                                  width: '12px',
                                  height: '12px',
                                },
                                '&::-webkit-scrollbar-track': {
                                  backgroundColor: isDark ? '#1C1D22' : '#f1f1f1',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                  backgroundColor: isDark ? '#555' : '#888',
                                  borderRadius: '6px',
                                  '&:hover': {
                                    backgroundColor: isDark ? '#777' : '#555',
                                  },
                                },
                              }}
                            >
                              <Table 
                                stickyHeader 
                                size="small" 
                                sx={{ 
                                  borderCollapse: 'separate', 
                                  borderSpacing: 0,
                                  minWidth: 'max-content',
                                }}
                              >
                                <TableHead>
                                  <TableRow>
                                    <TableCell
                                      sx={{ 
                                        backgroundColor: isDark ? '#1C1D22' : '#E8EAED', 
                                        fontWeight: 600, 
                                        border: `1px solid ${isDark ? '#555' : '#ddd'}`,
                                        borderRight: `1px solid ${isDark ? '#555' : '#ddd'}`,
                                        py: 0.75,
                                        px: 1.5,
                                        minWidth: 100,
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 10,
                                        boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                                      }}
                                    >
                                      작업
                                    </TableCell>
                                    {tableColumns.map((col) => (
                                      <TableCell
                                        key={col.name}
                                        sx={{ 
                                          backgroundColor: isDark ? '#1C1D22' : '#E8EAED', 
                                          fontWeight: 600, 
                                          border: `1px solid ${isDark ? '#555' : '#ddd'}`,
                                          borderRight: `1px solid ${isDark ? '#555' : '#ddd'}`,
                                          py: 0.75,
                                          px: 1.5,
                                          whiteSpace: 'nowrap',
                                          minWidth: 150,
                                          position: 'sticky',
                                          top: 0,
                                          zIndex: 9,
                                        }}
                                      >
                                        {col.name}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {/* 기존 데이터 행 */}
                                  {tableData.length > 0 ? (
                                    tableData.map((row, index) => (
                                      <TableRow 
                                        key={index} 
                                        sx={{ 
                                          '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' },
                                          '& .MuiTableCell-root': {
                                            py: 0.25,
                                            px: 1
                                          },
                                          ...(editingRowIndex === index ? {
                                            backgroundColor: isDark ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.05)'
                                          } : {})
                                        }}
                                      >
                                        <TableCell 
                                          sx={{ 
                                            border: `1px solid ${isDark ? '#555' : '#ddd'}`,
                                            borderRight: `1px solid ${isDark ? '#555' : '#ddd'}`,
                                            position: 'sticky', 
                                            left: 0, 
                                            zIndex: 8, 
                                            backgroundColor: editingRowIndex === index 
                                              ? (isDark ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.05)') 
                                              : (isDark ? '#1C1D22' : '#ffffff'),
                                            boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {editingRowIndex === index ? (
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                              <Tooltip title="저장">
                                                <IconButton size="small" onClick={handleSaveEditRow} sx={{ color: colors.accent }}>
                                                  <Save size={16} />
                                                </IconButton>
                                              </Tooltip>
                                              <Tooltip title="취소">
                                                <IconButton size="small" onClick={handleCancelEditRow} sx={{ color: colors.secondaryText }}>
                                                  <X size={16} />
                                                </IconButton>
                                              </Tooltip>
                                            </Box>
                                          ) : (
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                              <Tooltip title="수정">
                                                <IconButton size="small" onClick={() => handleEditRow(index)} sx={{ color: colors.accent }}>
                                                  <Edit size={16} />
                                                </IconButton>
                                              </Tooltip>
                                              <Tooltip title="삭제">
                                                <IconButton size="small" onClick={() => handleDeleteRow(row)} sx={{ color: colors.error || '#f44336' }}>
                                                  <Trash2 size={16} />
                                                </IconButton>
                                              </Tooltip>
                                            </Box>
                                          )}
                                        </TableCell>
                                        {tableColumns.map((col) => {
                                          const colInfo = tableColumnInfo.find(c => c.column_name === col.name);
                                          const isAutoIncrement = colInfo?.extra?.toLowerCase().includes('auto_increment') || false;
                                          const isPrimaryKey = primaryKeys.includes(col.name);
                                          
                                          return (
                                            <TableCell 
                                              key={col.name} 
                                              sx={{ 
                                                border: `1px solid ${isDark ? '#555' : '#ddd'}`,
                                                borderRight: `1px solid ${isDark ? '#555' : '#ddd'}`,
                                                py: 0.5,
                                                px: 1.5,
                                                whiteSpace: 'nowrap',
                                                backgroundColor: editingRowIndex === index 
                                                  ? (isDark ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.05)') 
                                                  : 'inherit',
                                              }}
                                            >
                                              {editingRowIndex === index ? (
                                                isAutoIncrement || isPrimaryKey ? (
                                                  <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                                    {row[col.name] !== null && row[col.name] !== undefined ? String(row[col.name]) : '-'}
                                                  </Typography>
                                                ) : (
                                                  <TextField
                                                    size="small"
                                                    value={editingRowData[col.name] !== null && editingRowData[col.name] !== undefined ? String(editingRowData[col.name]) : ''}
                                                    onChange={(e) => handleEditRowChange(col.name, e.target.value)}
                                                    sx={{ 
                                                      width: '100%',
                                                      '& .MuiInputBase-root': {
                                                        fontSize: '12px',
                                                        minHeight: '32px'
                                                      }
                                                    }}
                                                  />
                                                )
                                              ) : (
                                                <Typography 
                                                  variant="body2" 
                                                  sx={{ 
                                                    fontSize: '12px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    maxWidth: '300px',
                                                    display: 'block',
                                                  }}
                                                  title={row[col.name] !== null && row[col.name] !== undefined ? String(row[col.name]) : '-'}
                                                >
                                                  {row[col.name] !== null && row[col.name] !== undefined ? String(row[col.name]) : '-'}
                                                </Typography>
                                              )}
                                            </TableCell>
                                          );
                                        })}
                                      </TableRow>
                                    ))
                                  ) : (
                                    <TableRow>
                                      <TableCell 
                                        colSpan={tableColumns.length + 1} 
                                        align="center" 
                                        sx={{ 
                                          py: 4, 
                                          border: `1px solid ${isDark ? '#555' : '#ddd'}`, 
                                          color: colors.secondaryText, 
                                          fontSize: '14px' 
                                        }}
                                      >
                                        데이터가 없습니다. 행 추가 버튼을 눌러 데이터를 추가하세요.
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                ) : (
                  <Box 
                    sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '50%',
                      width: '50%',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      DB에 연결하여 테이블을 조회하고 관리할 수 있습니다.
                    </Typography>
                    <Button variant="contained" onClick={() => setConnectionDialogOpen(true)}>
                      DB 연결 설정
                    </Button>
                  </Box>
                )}
              </Box>
            )}
            
            {/* 권한 관리 탭 */}
            {tabValue === 1 && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">권한 관리</Typography>
                  <Button 
                    size="small" 
                    variant="outlined"
                    onClick={fetchPermissionData}
                    disabled={isLoadingPermissions}
                  >
                    새로고침
                  </Button>
                </Box>
                
                {isLoadingPermissions ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Paper sx={{ flex: 1, overflow: 'hidden' }}>
                    <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}>
                      <Table stickyHeader size="small" sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                        <TableHead>
                          <TableRow>
                            {/* 고정 컬럼 */}
                            <TableCell 
                              sx={{ 
                                backgroundColor: isDark ? '#1C1D22' : '#E8EAED', 
                                fontWeight: 600, 
                                border: '1px solid ' + (isDark ? '#555' : '#ccc'),
                                position: 'sticky',
                                left: 0,
                                zIndex: 3,
                                minWidth: 75,
                                width: 75,
                                textAlign: 'center'
                              }}
                            >
                              작업
                            </TableCell>
                            <TableCell 
                              sx={{ 
                                backgroundColor: isDark ? '#1C1D22' : '#E8EAED', 
                                fontWeight: 600, 
                                border: '1px solid ' + (isDark ? '#555' : '#ccc'),
                                position: 'sticky',
                                left: 75,
                                zIndex: 3,
                                minWidth: 50,
                                width: 50,
                                textAlign: 'center'
                              }}
                            >
                              연번
                            </TableCell>
                            <TableCell 
                              sx={{ 
                                backgroundColor: isDark ? '#1C1D22' : '#E8EAED', 
                                fontWeight: 600, 
                                border: '1px solid ' + (isDark ? '#555' : '#ccc'),
                                position: 'sticky',
                                left: 125,
                                zIndex: 3,
                                minWidth: 90,
                                width: 90
                              }}
                            >
                              성명
                            </TableCell>
                            <TableCell 
                              sx={{ 
                                backgroundColor: isDark ? '#1C1D22' : '#E8EAED', 
                                fontWeight: 600, 
                                border: '1px solid ' + (isDark ? '#555' : '#ccc'),
                                position: 'sticky',
                                left: 215,
                                zIndex: 3,
                                minWidth: 180,
                                width: 180
                              }}
                            >
                              이메일
                            </TableCell>
                            <TableCell 
                              sx={{ 
                                backgroundColor: isDark ? '#1C1D22' : '#E8EAED', 
                                fontWeight: 600, 
                                border: '1px solid ' + (isDark ? '#555' : '#ccc'),
                                position: 'sticky',
                                left: 395,
                                zIndex: 3,
                                minWidth: 70,
                                width: 70,
                                textAlign: 'center'
                              }}
                            >
                              Admin
                            </TableCell>
                            <TableCell 
                              sx={{ 
                                backgroundColor: isDark ? '#1C1D22' : '#E8EAED', 
                                fontWeight: 600, 
                                border: '1px solid ' + (isDark ? '#555' : '#ccc'),
                                position: 'sticky',
                                left: 465,
                                zIndex: 3,
                                minWidth: 80,
                                width: 80,
                                textAlign: 'center'
                              }}
                            >
                              Manager
                            </TableCell>
                            {/* 부서 컬럼 (스크롤 가능) */}
                            {departments.map((dept) => (
                              <TableCell
                                key={dept.dept_code}
                                sx={{ 
                                  backgroundColor: isDark ? '#1C1D22' : '#E8EAED', 
                                  fontWeight: 600, 
                                  border: '1px solid ' + (isDark ? '#555' : '#ccc'),
                                  minWidth: 100,
                                  width: 100,
                                  textAlign: 'center'
                                }}
                              >
                                {dept.dept_name}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {employees.length > 0 ? (
                            employees.map((emp, index) => {
                              const isEditing = editingEmployee === emp.employee_code;
                              // 디버깅: 편집 상태 확인
                              if (isEditing) {
                                console.log('[권한 편집] 편집 중인 행 렌더링:', emp.employee_code, 'editingEmployee:', editingEmployee);
                              }
                              return (
                              <TableRow 
                                key={emp.employee_code}
                                sx={{ 
                                  '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' },
                                  backgroundColor: isEditing ? (isDark ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)') : 'inherit'
                                }}
                              >
                                {/* 작업 컬럼 (편집/저장/취소 버튼) */}
                                <TableCell 
                                  sx={{ 
                                    border: '1px solid ' + (isDark ? '#555' : '#ccc'),
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 1,
                                    backgroundColor: isEditing 
                                      ? (isDark ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.08)')
                                      : (isDark ? '#1C1D22' : '#fff'),
                                    textAlign: 'center',
                                    minWidth: 75,
                                    width: 75
                                  }}
                                >
                                  {isEditing ? (
                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                      <Tooltip title="저장">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleSavePermissions(emp.employee_code)}
                                          sx={{
                                            color: colors.accent,
                                            '&:hover': { backgroundColor: isDark ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)' }
                                          }}
                                        >
                                          <Save size={16} />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="취소">
                                        <IconButton
                                          size="small"
                                          onClick={handleCancelEdit}
                                          sx={{
                                            color: isDark ? '#999' : '#666',
                                            '&:hover': { backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)' }
                                          }}
                                        >
                                          <X size={16} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  ) : (
                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                      <Tooltip title="권한 편집">
                                        <span>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleStartEdit(emp.employee_code)}
                                            disabled={
                                              emp.role === 'Root' || // Root는 변경 불가
                                              (currentUserRole !== 'Root' && currentUserRole !== 'Admin') // Root/Admin만 편집 가능
                                            }
                                            sx={{
                                              color: (currentUserRole === 'Root' || currentUserRole === 'Admin') && emp.role !== 'Root'
                                                ? colors.accent
                                                : (isDark ? '#555' : '#ccc'),
                                              '&:hover': {
                                                backgroundColor: (currentUserRole === 'Root' || currentUserRole === 'Admin') && emp.role !== 'Root'
                                                  ? (isDark ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                                                  : 'transparent'
                                              },
                                              '&.Mui-disabled': {
                                                color: isDark ? '#555' : '#ccc'
                                              }
                                            }}
                                          >
                                            <Edit size={16} />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Box>
                                  )}
                                </TableCell>
                                {/* 고정 컬럼 */}
                                <TableCell 
                                  sx={{ 
                                    border: '1px solid ' + (isDark ? '#555' : '#ccc'),
                                    position: 'sticky',
                                    left: 75,
                                    zIndex: 1,
                                    backgroundColor: isEditing 
                                      ? (isDark ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.08)')
                                      : (isDark ? '#1C1D22' : '#fff'),
                                    textAlign: 'center',
                                    minWidth: 50,
                                    width: 50
                                  }}
                                >
                                  {index + 1}
                                </TableCell>
                                <TableCell 
                                  sx={{ 
                                    border: '1px solid ' + (isDark ? '#555' : '#ccc'),
                                    position: 'sticky',
                                    left: 125,
                                    zIndex: 1,
                                    backgroundColor: isEditing 
                                      ? (isDark ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.08)')
                                      : (isDark ? '#1C1D22' : '#fff'),
                                    minWidth: 90,
                                    width: 90
                                  }}
                                >
                                  {emp.employee_name}
                                </TableCell>
                                <TableCell 
                                  sx={{ 
                                    border: '1px solid ' + (isDark ? '#555' : '#ccc'),
                                    position: 'sticky',
                                    left: 215,
                                    zIndex: 1,
                                    backgroundColor: isEditing 
                                      ? (isDark ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.08)')
                                      : (isDark ? '#1C1D22' : '#fff'),
                                    minWidth: 180,
                                    width: 180
                                  }}
                                >
                                  {emp.email}
                                </TableCell>
                                <TableCell 
                                  sx={{ 
                                    border: '1px solid ' + (isDark ? '#555' : '#ccc'),
                                    position: 'sticky',
                                    left: 395,
                                    zIndex: 1,
                                    backgroundColor: isEditing 
                                      ? (isDark ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.08)')
                                      : (isDark ? '#1C1D22' : '#fff'),
                                    textAlign: 'center',
                                    minWidth: 70,
                                    width: 70
                                  }}
                                >
                                  {isEditing ? (
                                    <Checkbox
                                      checked={editPermissions.isAdmin || false}
                                      onChange={(e) => handleEditPermissionChange('admin', e.target.checked)}
                                      disabled={
                                        emp.role === 'Root' || // Root는 변경 불가
                                        currentUserRole !== 'Root' // Root만 Admin 권한 변경 가능
                                      }
                                      size="small"
                                    />
                                  ) : (
                                    <Checkbox
                                      checked={emp.isAdmin}
                                      disabled
                                      size="small"
                                    />
                                  )}
                                </TableCell>
                                <TableCell 
                                  sx={{ 
                                    border: '1px solid ' + (isDark ? '#555' : '#ccc'),
                                    position: 'sticky',
                                    left: 465,
                                    zIndex: 1,
                                    backgroundColor: isEditing 
                                      ? (isDark ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.08)')
                                      : (isDark ? '#1C1D22' : '#fff'),
                                    textAlign: 'center',
                                    fontWeight: (isEditing 
                                      ? (editPermissions.deptManagerMap && Object.values(editPermissions.deptManagerMap).some(v => v === true))
                                      : emp.isManager) ? 600 : 400,
                                    color: (isEditing 
                                      ? (editPermissions.deptManagerMap && Object.values(editPermissions.deptManagerMap).some(v => v === true))
                                      : emp.isManager) ? (isDark ? '#4CAF50' : '#2E7D32') : 'inherit',
                                    minWidth: 80,
                                    width: 80
                                  }}
                                >
                                  {isEditing ? (() => {
                                    const managerValue = editPermissions.deptManagerMap && Object.values(editPermissions.deptManagerMap).some(v => v === true) ? 'Y' : 'N';
                                    return (
                                      <FormControl size="small" fullWidth>
                                        <Select
                                          value={managerValue}
                                          onChange={(e) => handleEditPermissionChange('managerAll', e.target.value)}
                                          disabled={currentUserRole !== 'Root' && currentUserRole !== 'Admin'}
                                          sx={{
                                            '& .MuiSelect-select': {
                                              textAlign: 'center',
                                              py: 0.5,
                                              fontWeight: managerValue === 'Y' ? 600 : 400,
                                              color: managerValue === 'Y' ? (isDark ? '#4CAF50' : '#2E7D32') : 'inherit'
                                            }
                                          }}
                                        >
                                          <MenuItem value="Y">Y</MenuItem>
                                          <MenuItem value="N">N</MenuItem>
                                        </Select>
                                      </FormControl>
                                    );
                                  })() : (
                                    emp.isManager ? 'Y' : 'N'
                                  )}
                                </TableCell>
                                {/* 부서별 Manager 체크박스 */}
                                {departments.map((dept) => (
                                  <TableCell
                                    key={dept.dept_code}
                                    sx={{ 
                                      border: '1px solid ' + (isDark ? '#555' : '#ccc'),
                                      textAlign: 'center',
                                      backgroundColor: isEditing 
                                        ? (isDark ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.08)')
                                        : 'inherit',
                                      minWidth: 100,
                                      width: 100
                                    }}
                                  >
                                    {isEditing ? (
                                      <Checkbox
                                        checked={editPermissions.deptManagerMap?.[dept.dept_code] || false}
                                        onChange={(e) => handleEditPermissionChange('manager', e.target.checked, dept.dept_code)}
                                        disabled={
                                          currentUserRole !== 'Root' && currentUserRole !== 'Admin' // Root/Admin만 변경 가능
                                        }
                                        size="small"
                                      />
                                    ) : (
                                      <Checkbox
                                        checked={emp.deptManagerMap[dept.dept_code] || false}
                                        disabled
                                        size="small"
                                      />
                                    )}
                                  </TableCell>
                                ))}
                              </TableRow>
                            );
                            })
                          ) : (
                            <TableRow>
                              <TableCell 
                                colSpan={6 + departments.length} 
                                align="center" 
                                sx={{ py: 4, border: '1px solid ' + (isDark ? '#555' : '#ccc') }}
                              >
                                데이터가 없습니다.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}
              </Box>
            )}
            
            {/* 접속 로그 탭 */}
            {tabValue === 2 && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h6">접속 로그</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Autocomplete
                      size="small"
                      options={accessLogEmployees}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return `${option.employee_name || ''} (${option.employee_code || ''})`;
                      }}
                      value={accessLogEmployees.find(emp => emp.employee_code === accessLogFilter.employee_code) || null}
                      onChange={(event, newValue) => {
                        setAccessLogFilter({ 
                          ...accessLogFilter, 
                          employee_code: newValue ? newValue.employee_code : '' 
                        });
                      }}
                      filterOptions={(options, params) => {
                        const filtered = options.filter((option) => {
                          const searchText = params.inputValue.toLowerCase();
                          const name = (option.employee_name || '').toLowerCase();
                          const code = (option.employee_code || '').toLowerCase();
                          return name.includes(searchText) || code.includes(searchText);
                        });
                        return filtered;
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="이름 또는 코드"
                          placeholder="직원 이름 또는 코드로 검색"
                        />
                      )}
                      sx={{ minWidth: 200 }}
                      clearOnEscape
                      clearOnBlur={false}
                    />
                    <TextField
                      size="small"
                      label="시작일"
                      type="date"
                      value={accessLogFilter.start_date}
                      onChange={(e) => setAccessLogFilter({ ...accessLogFilter, start_date: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 150 }}
                    />
                    <TextField
                      size="small"
                      label="종료일"
                      type="date"
                      value={accessLogFilter.end_date}
                      onChange={(e) => setAccessLogFilter({ ...accessLogFilter, end_date: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 150 }}
                    />
                    <Button 
                      variant="contained"
                      onClick={fetchAccessLogs}
                      disabled={isLoadingAccessLogs}
                      size="small"
                    >
                      조회
                    </Button>
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={fetchAccessLogs}
                      disabled={isLoadingAccessLogs}
                    >
                      새로고침
                    </Button>
                  </Box>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  총 {accessLogTotal}건
                </Typography>
                
                {isLoadingAccessLogs ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Paper sx={{ flex: 1, overflow: 'auto' }}>
                    <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>로그ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>직원코드</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>성명</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>이메일</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>접속일시</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>브라우저</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>버전</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>IP주소</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>User-Agent</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {accessLogs.length > 0 ? (
                            accessLogs.map((log) => (
                              <TableRow key={log.log_id}>
                                <TableCell>{log.log_id}</TableCell>
                                <TableCell>{log.employee_code}</TableCell>
                                <TableCell>{log.employee_name || '-'}</TableCell>
                                <TableCell>{log.email}</TableCell>
                                <TableCell>
                                  {log.access_date ? new Date(log.access_date).toLocaleString('ko-KR') : '-'}
                                </TableCell>
                                <TableCell>{log.browser_name || '-'}</TableCell>
                                <TableCell>{log.browser_version || '-'}</TableCell>
                                <TableCell>{log.ip_address || '-'}</TableCell>
                                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {log.user_agent || '-'}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                접속 로그가 없습니다.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}
              </Box>
            )}

            {/* 알림 관리 탭 */}
            {tabValue === 3 && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h6">알림 관리</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Bell size={18} />}
                      onClick={async () => {
                        // 다이얼로그를 열 때 현재 저장된 값 다시 불러오기
                        const config = await fetchNotificationTimes();
                        // 다이얼로그 내부 상태를 최신 값으로 초기화
                        setDialogNotificationTimes([...config.times]);
                        setExcludeSaturday(config.excludeSaturday);
                        setExcludeSunday(config.excludeSunday);
                        setNotificationTimeDialogOpen(true);
                      }}
                      sx={{ borderColor: colors.accent, color: colors.accent }}
                    >
                      알림 시간 설정
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Plus size={18} />}
                      onClick={handleCreateAnnouncement}
                      sx={{ backgroundColor: colors.accent }}
                    >
                      공지사항 등록
                    </Button>
                  </Box>
                </Box>

                {/* 알림 시간 설정 카드 */}
                <Card sx={{ backgroundColor: isDark ? colors.secondaryBg : colors.primaryBg }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                        투입시간 알림 시간 설정
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Edit size={16} />}
                        onClick={async () => {
                          // 다이얼로그를 열 때 현재 저장된 값 다시 불러오기
                          const config = await fetchNotificationTimes();
                          // 다이얼로그 내부 상태를 최신 값으로 초기화
                          setDialogNotificationTimes([...config.times]);
                          setExcludeSaturday(config.excludeSaturday);
                          setExcludeSunday(config.excludeSunday);
                          setNotificationTimeDialogOpen(true);
                        }}
                      >
                        수정
                      </Button>
                    </Box>
                    {isLoadingNotificationTimes ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {notificationTimes.map((time, index) => (
                          <Chip
                            key={index}
                            label={`${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`}
                            color="primary"
                            size="small"
                            onDelete={() => {
                              const updated = notificationTimes.filter((_, i) => i !== index);
                              setNotificationTimes(updated);
                              // 즉시 저장
                              handleSaveNotificationTimesDirect(updated);
                            }}
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}
                        {notificationTimes.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            설정된 알림 시간이 없습니다.
                          </Typography>
                        )}
                        <Chip
                          label="+ 추가"
                          color="primary"
                          variant="outlined"
                          size="small"
                          onClick={async () => {
                            // 다이얼로그를 열 때 현재 저장된 값 다시 불러오기
                            const config = await fetchNotificationTimes();
                            // 다이얼로그 내부 상태를 최신 값으로 초기화
                            setDialogNotificationTimes([...config.times]);
                            setExcludeSaturday(config.excludeSaturday);
                            setExcludeSunday(config.excludeSunday);
                            setNotificationTimeDialogOpen(true);
                          }}
                          sx={{ cursor: 'pointer' }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* FCM 알림 수신 테스트 */}
                <Card sx={{ backgroundColor: isDark ? colors.secondaryBg : colors.primaryBg }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                        FCM 알림 수신 테스트
                      </Typography>
                      <Chip
                        label={`권한: ${notificationPermissionLabel}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: notificationPermission === 'granted' ? colors.accent : colors.primaryBorder,
                          color: notificationPermission === 'granted' ? colors.accent : colors.secondaryText
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: colors.secondaryText, mb: 2 }}>
                      개발환경에서 FCM 토큰 등록과 실제 알림 수신을 테스트합니다.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="대상 직원 코드"
                        value={fcmTestEmployeeCode}
                        onChange={(e) => setFcmTestEmployeeCode(e.target.value)}
                        placeholder="예: EMP001"
                        size="small"
                        helperText="FCM 테스트 알림을 받을 직원의 코드"
                      />
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleUseMyEmployeeCode}
                        >
                          내 계정 자동 입력
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleRegisterFcmToken}
                          disabled={isSavingFcmToken}
                          startIcon={isSavingFcmToken ? <CircularProgress size={14} /> : <CheckCircle size={14} />}
                        >
                          {isSavingFcmToken ? '등록 중...' : 'FCM 토큰 등록/갱신'}
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleSendFcmTest}
                          disabled={isSendingFcmTest}
                          startIcon={isSendingFcmTest ? <CircularProgress size={14} /> : <Bell size={14} />}
                          sx={{ backgroundColor: colors.accent }}
                        >
                          {isSendingFcmTest ? '전송 중...' : '테스트 알림 보내기'}
                        </Button>
                      </Box>
                      <TextField
                        label="알림 제목"
                        value={fcmTestTitle}
                        onChange={(e) => setFcmTestTitle(e.target.value)}
                        size="small"
                      />
                      <TextField
                        label="알림 내용"
                        value={fcmTestBody}
                        onChange={(e) => setFcmTestBody(e.target.value)}
                        size="small"
                        multiline
                        minRows={2}
                      />
                      {fcmTestStatus && (
                        <Alert severity={fcmTestStatus.severity}>
                          {fcmTestStatus.message}
                        </Alert>
                      )}
                    </Box>
                  </CardContent>
                </Card>

                {/* 공지사항 섹션 */}
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mt: 2 }}>
                  공지사항 관리
                </Typography>

                {isLoadingAnnouncements ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Paper sx={{ flex: 1, overflow: 'auto' }}>
                    <TableContainer>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>제목</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>시작일</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>종료일</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>상태</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>미읽음 수</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>생성일</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="center">작업</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {announcements.length > 0 ? (
                            announcements.map((announcement) => (
                              <TableRow key={announcement.announcement_id}>
                                <TableCell>{announcement.announcement_id}</TableCell>
                                <TableCell>{announcement.title}</TableCell>
                                <TableCell>{announcement.start_date}</TableCell>
                                <TableCell>{announcement.end_date}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={announcement.is_active ? '활성' : '비활성'}
                                    color={announcement.is_active ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={announcement.unread_count || 0}
                                    color="error"
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {announcement.created_at ? new Date(announcement.created_at).toLocaleDateString('ko-KR') : '-'}
                                </TableCell>
                                <TableCell align="center">
                                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                    <Tooltip title="수정">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEditAnnouncement(announcement)}
                                        sx={{ color: colors.accent }}
                                      >
                                        <Edit size={16} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="삭제">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeleteAnnouncement(announcement.announcement_id)}
                                        sx={{ color: colors.error }}
                                      >
                                        <Trash2 size={16} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                등록된 공지사항이 없습니다.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* 공지사항 등록/수정 다이얼로그 */}
      <Dialog
        open={announcementDialogOpen}
        onClose={() => setAnnouncementDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSaveAnnouncement();
          }}
        >
          <DialogTitle>
            {editingAnnouncement ? '공지사항 수정' : '공지사항 등록'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="제목"
                fullWidth
                required
                value={announcementForm.title}
                onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
              />
              <TextField
                label="내용"
                fullWidth
                required
                multiline
                rows={6}
                value={announcementForm.message}
                onChange={(e) => setAnnouncementForm({ ...announcementForm, message: e.target.value })}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="시작일"
                  type="date"
                  fullWidth
                  required
                  value={announcementForm.start_date}
                  onChange={(e) => setAnnouncementForm({ ...announcementForm, start_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="종료일"
                  type="date"
                  fullWidth
                  required
                  value={announcementForm.end_date}
                  onChange={(e) => setAnnouncementForm({ ...announcementForm, end_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <FormControl fullWidth>
                <InputLabel>상태</InputLabel>
                <Select
                  value={announcementForm.is_active ? 1 : 0}
                  onChange={(e) => setAnnouncementForm({ ...announcementForm, is_active: e.target.value === 1 })}
                  label="상태"
                >
                  <MenuItem value={1}>활성</MenuItem>
                  <MenuItem value={0}>비활성</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAnnouncementDialogOpen(false)}>
              취소
            </Button>
            <Button type="submit" variant="contained" sx={{ backgroundColor: colors.accent }}>
              저장
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* 알림 시간 설정 다이얼로그 */}
      <Dialog
        open={notificationTimeDialogOpen}
        onClose={() => setNotificationTimeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            console.log('[Admin] 폼 제출 이벤트 발생 - 알림 시간 설정 저장');
            await handleSaveNotificationTimes();
          }}
        >
          <DialogTitle>
            투입시간 알림 시간 설정
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                매일 지정된 시간에 투입시간 입력을 독려하는 알림이 표시됩니다.
              </Typography>
              
              {dialogNotificationTimes.map((time, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>시간</InputLabel>
                    <Select
                      value={time.hour}
                      onChange={(e) => handleUpdateNotificationTime(index, 'hour', e.target.value)}
                      label="시간"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <MenuItem key={i} value={i}>
                          {String(i).padStart(2, '0')}시
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>분</InputLabel>
                    <Select
                      value={time.minute}
                      onChange={(e) => handleUpdateNotificationTime(index, 'minute', e.target.value)}
                      label="분"
                    >
                      {Array.from({ length: 60 }, (_, i) => (
                        <MenuItem key={i} value={i}>
                          {String(i).padStart(2, '0')}분
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveNotificationTime(index)}
                    sx={{ color: colors.error }}
                  >
                    <Trash2 size={18} />
                  </IconButton>
                </Box>
              ))}
              
              {dialogNotificationTimes.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  알림 시간이 없습니다. 아래 버튼을 클릭하여 추가하세요.
                </Typography>
              )}
              
              <Button
                variant="outlined"
                startIcon={<Plus size={18} />}
                onClick={handleAddNotificationTime}
                sx={{ mt: 1 }}
              >
                알림 시간 추가
              </Button>
              
              <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${colors.border}` }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                  주말 알림 제외 설정
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={excludeSaturday}
                      onChange={(e) => setExcludeSaturday(e.target.checked)}
                      size="small"
                    />
                    <Typography variant="body2">
                      토요일 제외
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={excludeSunday}
                      onChange={(e) => setExcludeSunday(e.target.checked)}
                      size="small"
                    />
                    <Typography variant="body2">
                      일요일 제외
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                console.log('[Admin] 취소 버튼 클릭');
                // 취소 시 다이얼로그 내부 상태를 초기화하지 않고 그냥 닫기
                // (다음에 열 때 최신 값으로 다시 초기화됨)
                setNotificationTimeDialogOpen(false);
              }}
            >
              취소
            </Button>
            <Button 
              type="button"
              variant="contained" 
              sx={{ backgroundColor: colors.accent }}
              disabled={isLoadingNotificationTimes}
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('[Admin] 저장 버튼 클릭됨 - 직접 호출', {
                  dialogNotificationTimes,
                  count: dialogNotificationTimes.length
                });
                try {
                  await handleSaveNotificationTimes();
                } catch (error) {
                  console.error('[Admin] 저장 버튼 클릭 시 에러:', error);
                  setIsLoadingNotificationTimes(false);
                }
              }}
            >
              {isLoadingNotificationTimes ? '저장 중...' : '저장'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* DB 연결 설정 다이얼로그 */}
      <Dialog open={connectionDialogOpen} onClose={() => setConnectionDialogOpen(false)} maxWidth="sm" fullWidth>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!isConnecting) {
              handleConnectDB();
            }
          }}
        >
          <DialogTitle>DB 연결정보 설정</DialogTitle>
          <DialogContent>
            {connectionError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {/* 문자열로 변환하여 안전하게 렌더링 */}
                {String(connectionError).split('\n').map((line, idx) => (
                  <div key={idx}>{line}</div>
                ))}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>DB 타입</InputLabel>
                <Select
                  value={dbConnectionInfo.dbType}
                  onChange={(e) => setDbConnectionInfo({ ...dbConnectionInfo, dbType: e.target.value })}
                  label="DB 타입"
                >
                  <MenuItem value="mysql">MySQL</MenuItem>
                  <MenuItem value="mssql">MS SQL Server</MenuItem>
                  <MenuItem value="postgresql">PostgreSQL</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="호스트"
                value={dbConnectionInfo.host}
                onChange={(e) => setDbConnectionInfo({ ...dbConnectionInfo, host: e.target.value })}
                size="small"
                fullWidth
              />
              <TextField
                label="포트"
                type="number"
                value={dbConnectionInfo.port}
                onChange={(e) => setDbConnectionInfo({ ...dbConnectionInfo, port: parseInt(e.target.value) || 3306 })}
                size="small"
                fullWidth
              />
              <TextField
                label="데이터베이스"
                value={dbConnectionInfo.database}
                onChange={(e) => setDbConnectionInfo({ ...dbConnectionInfo, database: e.target.value })}
                size="small"
                fullWidth
              />
              <TextField
                label="사용자명"
                value={dbConnectionInfo.username}
                onChange={(e) => setDbConnectionInfo({ ...dbConnectionInfo, username: e.target.value })}
                size="small"
                fullWidth
              />
              <TextField
                label="비밀번호"
                type="password"
                value={dbConnectionInfo.password}
                onChange={(e) => setDbConnectionInfo({ ...dbConnectionInfo, password: e.target.value })}
                size="small"
                fullWidth
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isConnecting) {
                    e.preventDefault();
                    handleConnectDB();
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={() => setConnectionDialogOpen(false)}>취소</Button>
            <Button type="submit" variant="contained" disabled={isConnecting}>
              {isConnecting ? <CircularProgress size={20} /> : '연결'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* 일괄 업로드 결과 다이얼로그 */}
      <Dialog open={uploadAllDialogOpen} onClose={() => setUploadAllDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>일괄 업로드 결과</DialogTitle>
        <DialogContent>
          {uploadAllResults && (
            <Box>
              <Alert 
                severity={uploadAllResults.failed_tables === 0 ? "success" : "warning"} 
                sx={{ mb: 2 }}
              >
                {uploadAllResults.message}
              </Alert>
              
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                상세 결과:
              </Typography>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>테이블명</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">상태</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">삽입 건수</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">중복 건수</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">실패 건수</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>오류 메시지</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uploadAllResults.results.map((result, index) => (
                      <TableRow key={index}>
                        <TableCell>{result.table}</TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={result.success ? "성공" : "실패"} 
                            color={result.success ? "success" : "error"} 
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color={result.inserted_count > 0 ? "success.main" : "text.secondary"}>
                            {result.inserted_count || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {result.skipped_count > 0 ? (
                            <Typography variant="body2" color="warning.main">
                              {result.skipped_count}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {result.failed_count > 0 ? (
                            <Typography variant="body2" color="error">
                              {result.failed_count}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {result.error ? (
                            <Typography 
                              variant="caption" 
                              color="error"
                              sx={{ 
                                display: 'block',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                maxWidth: '400px'
                              }}
                            >
                              {result.error}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadAllDialogOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 에러 모달 다이얼로그 */}
      <Dialog 
        open={errorDialogOpen} 
        onClose={() => setErrorDialogOpen(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AlertTriangle size={20} color={colors.error} />
          오류 발생
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            DB 연결 설정에서 올바른 비밀번호를 입력하고 연결을 시도해주세요.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogOpen(false)} variant="contained" color="primary">
            확인
          </Button>
          <Button 
            onClick={() => {
              setErrorDialogOpen(false);
              setConnectionDialogOpen(true);
            }} 
            variant="outlined"
          >
            DB 연결 설정
          </Button>
        </DialogActions>
      </Dialog>

      {/* 일괄 업로드 미리보기 다이얼로그 */}
      <Dialog 
        open={uploadPreviewDialogOpen} 
        onClose={() => {
          setUploadPreviewDialogOpen(false);
          setUploadPreviewData(null);
          setPendingUploadFile(null);
          setSelectedTablesForUpload(new Set());
        }} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">업로드 데이터 미리보기</Typography>
            <Typography variant="body2" color="text.secondary">
              {uploadPreviewData ? Object.keys(uploadPreviewData).length : 0}개 테이블
              {selectedTablesForUpload.size > 0 && (
                <span style={{ marginLeft: '8px', color: colors.accent }}>
                  ({selectedTablesForUpload.size}개 선택됨)
                </span>
              )}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', height: 'calc(90vh - 120px)' }}>
          {uploadPreviewData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* 테이블 선택 탭 (체크박스 포함) */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Checkbox
                    checked={selectedTablesForUpload.size === Object.keys(uploadPreviewData).length && Object.keys(uploadPreviewData).length > 0}
                    indeterminate={selectedTablesForUpload.size > 0 && selectedTablesForUpload.size < Object.keys(uploadPreviewData).length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedTablesForUpload(new Set(Object.keys(uploadPreviewData)));
                      } else {
                        setSelectedTablesForUpload(new Set());
                      }
                    }}
                    size="small"
                  />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    전체 선택 ({selectedTablesForUpload.size}/{Object.keys(uploadPreviewData).length})
                  </Typography>
                </Box>
                <Tabs 
                  value={selectedPreviewTable || Object.keys(uploadPreviewData)[0]} 
                  onChange={(e, newValue) => setSelectedPreviewTable(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {Object.keys(uploadPreviewData).map((tableName) => (
                    <Tab 
                      key={tableName}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox
                            checked={selectedTablesForUpload.has(tableName)}
                            onChange={(e) => {
                              e.stopPropagation();
                              const newSelected = new Set(selectedTablesForUpload);
                              if (e.target.checked) {
                                newSelected.add(tableName);
                              } else {
                                newSelected.delete(tableName);
                              }
                              setSelectedTablesForUpload(newSelected);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                            sx={{ p: 0 }}
                          />
                          <span>{tableName} ({uploadPreviewData[tableName].length}건)</span>
                        </Box>
                      }
                      value={tableName}
                      sx={{ textTransform: 'none', minHeight: '48px' }}
                    />
                  ))}
                </Tabs>
              </Box>
                
                {/* 선택된 테이블의 데이터 표시 */}
                {selectedPreviewTable && uploadPreviewData[selectedPreviewTable] && (
                  <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      테이블: {selectedPreviewTable} ({uploadPreviewData[selectedPreviewTable].length}건)
                    </Typography>
                    
                    <TableContainer component={Paper} sx={{ maxHeight: 'calc(90vh - 250px)' }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            {uploadPreviewData[selectedPreviewTable].length > 0 && 
                              Object.keys(uploadPreviewData[selectedPreviewTable][0]).map((col) => (
                                <TableCell key={col} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                  {col}
                                </TableCell>
                              ))
                            }
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {uploadPreviewData[selectedPreviewTable].slice(0, 100).map((row, index) => (
                            <TableRow key={index} hover>
                              {Object.keys(uploadPreviewData[selectedPreviewTable][0]).map((col) => (
                                <TableCell 
                                  key={col}
                                  sx={{ 
                                    maxWidth: 200, 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={String(row[col] || '')}
                                >
                                  {String(row[col] || '')}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                          {uploadPreviewData[selectedPreviewTable].length > 100 && (
                            <TableRow>
                              <TableCell 
                                colSpan={Object.keys(uploadPreviewData[selectedPreviewTable][0]).length}
                                align="center"
                                sx={{ py: 2, color: 'text.secondary', fontStyle: 'italic' }}
                              >
                                ... 외 {uploadPreviewData[selectedPreviewTable].length - 100}건 더 있습니다 (미리보기는 최대 100건만 표시)
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {uploadPreviewData[selectedPreviewTable].length === 0 && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        이 테이블에는 데이터가 없습니다.
                      </Alert>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1.5 }}>
            <Button 
              onClick={() => {
                setUploadPreviewDialogOpen(false);
                setUploadPreviewData(null);
                setPendingUploadFile(null);
                setSelectedTablesForUpload(new Set());
              }}
              disabled={isUploadingAll}
            >
              취소
            </Button>
            <Button 
              onClick={handleUploadSelectedTables}
              variant="contained"
              disabled={isUploadingAll || !pendingUploadFile || selectedTablesForUpload.size === 0}
              sx={{ backgroundColor: colors.accent }}
            >
              {isUploadingAll ? '업로드 중...' : '업로드 실행'}
            </Button>
            <Button 
              onClick={handleUploadAllTables}
              variant="contained"
              color="secondary"
              disabled={isUploadingAll || !pendingUploadFile}
              sx={{ ml: 1 }}
            >
              {isUploadingAll ? '업로드 중...' : '일괄 업로드 실행'}
            </Button>
          </DialogActions>
      </Dialog>
      
      {/* 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="삭제 확인"
        message={deleteConfirmMessage}
        confirmText="삭제"
        cancelText="취소"
        confirmColor="#f44336"
      />
    </Box>
  );
};

export default Admin;
